/**
 * Rambo Ring Widget
 * Replaces native Shopify variant selector with custom ring style swatches + size dropdown
 * Uses data from Rambo Ring backend API
 */

(function() {
  'use strict';
  
  // SKIP on mobile - mobile has its own widget in tp-product-mobile.liquid
  if (window.innerWidth < 640) {
    console.log('[RamboWidget] Skipping on mobile - using mobile-specific code');
    return;
  }

  // Shared state
  const widgetState = {
    selectedStyleIndex: -1,
    selectedSizeCode: '',
    selectedStyleLabel: '',
    selectedStyleCode: '',
    userManuallySelectedStyle: false,
    lastManualStyleIndex: -1
  };
  
  // Expose state globally for favorites button and other scripts
  window.ramboRingWidgetState = widgetState;
  
  /**
   * AGGRESSIVE VISUAL STATE SETTER - This function ALWAYS sets the visual state correctly
   * Call this whenever widgetState.selectedStyleIndex changes or visual state might be wrong
   */
  function forceSetVisualState() {
    const sectionEl = document.getElementById(SECTION_ID);
    if (!sectionEl) return false;
    
    const stylePicker = sectionEl.querySelector('#rambo-ring-style-picker');
    if (!stylePicker) return false;
    
    if (widgetState.selectedStyleIndex < 0) return false;
    
    const allSwatches = stylePicker.querySelectorAll('.ring-style-picker__swatch');
    if (allSwatches.length === 0) return false;
    
    const targetSwatch = allSwatches[widgetState.selectedStyleIndex];
    if (!targetSwatch) {
      console.warn('[RamboWidget] ⚠️ Target swatch not found at index', widgetState.selectedStyleIndex);
      return false;
    }
    
    // REMOVE active from ALL swatches first - CLEAN approach
    allSwatches.forEach(s => {
      s.classList.remove('ring-style-picker__swatch--active');
      s.removeAttribute('data-active');
      s.removeAttribute('aria-selected');
      // Clear ALL inline styles to let CSS handle it
      s.style.outline = 'none';
      s.style.outlineOffset = '';
      s.style.border = '';
      s.style.boxShadow = '';
    });
    
    // SET active on target swatch - CSS handles the visual
    targetSwatch.classList.add('ring-style-picker__swatch--active');
    targetSwatch.setAttribute('data-active', 'true');
    targetSwatch.setAttribute('aria-selected', 'true');
    // Let CSS handle the styling - no inline styles needed
    targetSwatch.style.outline = 'none';
    
    // Update selected style name display
    if (typeof updateSelectedStyleName === 'function') {
      updateSelectedStyleName(widgetState.selectedStyleIndex);
    }
    
    // Visual state set (removed verbose logging)
    return true;
  }
  
  // Expose globally so it can be called from anywhere
  window.forceSetRamboVisualState = forceSetVisualState;
  
  // RE-ENTRY GUARD: Prevents infinite loop from MutationObserver
  let isUpdatingVisualState = false;
  
  // INITIALIZATION GUARD: Prevents duplicate widget creation
  let widgetInitialized = false;
  
  // MUTATION OBSERVER: Watch for changes to active class and restore if needed
  let visualStateObserver = null;
  function startVisualStateWatcher() {
    if (visualStateObserver) return; // Already watching
    
    visualStateObserver = new MutationObserver((mutations) => {
      // CRITICAL: Skip if we're already updating (prevents infinite loop)
      if (isUpdatingVisualState) return;
      if (widgetState.selectedStyleIndex < 0) return;
      
      const sectionEl = document.getElementById(SECTION_ID);
      if (!sectionEl) return;
      
      const stylePicker = sectionEl.querySelector('#rambo-ring-style-picker');
      if (!stylePicker) return;
      
      const activeSwatch = stylePicker.querySelector('.ring-style-picker__swatch--active');
      const allSwatches = stylePicker.querySelectorAll('.ring-style-picker__swatch');
      const targetSwatch = allSwatches[widgetState.selectedStyleIndex];
      
      // If the wrong swatch is active or no swatch is active, fix it
      if (targetSwatch && (!activeSwatch || activeSwatch !== targetSwatch)) {
        isUpdatingVisualState = true;
        forceSetVisualState();
        setTimeout(() => { isUpdatingVisualState = false; }, 100);
      }
    });
    
    // Watch the entire style picker for class changes
    const sectionEl = document.getElementById(SECTION_ID);
    if (sectionEl) {
      const stylePicker = sectionEl.querySelector('#rambo-ring-style-picker');
      if (stylePicker) {
        visualStateObserver.observe(stylePicker, {
          childList: false,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style']
        });
        console.log('[RamboWidget] ✅✅✅ Visual state watcher started');
      }
    }
  }
  
  // Start watcher after a short delay to ensure DOM is ready
  setTimeout(() => {
    startVisualStateWatcher();
  }, 500);

  /**
   * LOCALSTORAGE PERSISTENCE - Save user selections
   */
  function getProductHandle() {
    return window.Shopify?.routes?.product || 
           document.querySelector('[data-product-handle]')?.dataset?.productHandle ||
           window.location.pathname.split('/products/')[1]?.split('?')[0] ||
           'default';
  }

  function getStorageKey(productHandle) {
    return `rambo_ring_selections_${productHandle || 'default'}`;
  }

  function saveSelectionsToStorage() {
    try {
      const productHandle = getProductHandle();
      
      // Find section - try SECTION_ID first, then fallback to finding any product section
      let sectionEl = null;
      if (SECTION_ID) {
        sectionEl = document.querySelector(`#${SECTION_ID}`);
      }
      if (!sectionEl) {
        // Fallback: find any product section
        sectionEl = document.querySelector('[id*="main-product"]') || 
                   document.querySelector('[id*="main_product"]') ||
                   document.querySelector('form[action*="/cart/add"]')?.closest('[id^="shopify-section-"]');
      }
      
      // Try multiple selectors - engraving section, sectionEl, then document
      const engravingSection = (sectionEl && sectionEl.querySelector('.tp-engraving-section')) || 
                                document.querySelector('.tp-engraving-section');
      
      const engravingInput = (engravingSection && engravingSection.querySelector('.tp-engraving-input')) ||
                             (sectionEl && sectionEl.querySelector('.tp-engraving-input')) || 
                             document.querySelector('.tp-engraving-input');
      
      const engravingFontSelect = (engravingSection && engravingSection.querySelector('#engraving-font-input')) ||
                                  (sectionEl && (sectionEl.querySelector('#engraving-font-input') || 
                                                  sectionEl.querySelector('select[id*="engraving-font"]'))) ||
                                  document.querySelector('#engraving-font-input') || 
                                  document.querySelector('select[id*="engraving-font"]');
      
      // Also get size from the hidden input - try multiple ways
      let sizeInput = null;
      
      if (sectionEl) {
        sizeInput = sectionEl.querySelector('#rambo-ring-size-input');
      }
      if (!sizeInput) {
        sizeInput = document.querySelector('#rambo-ring-size-input');
      }
      
      // PRIORITY: Use widgetState.selectedSizeCode first (set synchronously on click)
      // This is the most reliable source since it's set immediately when the button is clicked
      let actualSizeCode = '';
      
      // FIRST PRIORITY: widgetState.selectedSizeCode (set synchronously when size button is clicked)
      if (widgetState.selectedSizeCode) {
        actualSizeCode = widgetState.selectedSizeCode;
        console.log('[RamboWidget] ✅ Size from widgetState (PRIMARY):', actualSizeCode);
        
        // Also ensure input is in sync (for form submission)
        if (sizeInput && sizeInput.value !== actualSizeCode) {
          sizeInput.value = actualSizeCode;
          console.log('[RamboWidget] Synced input value to:', actualSizeCode);
        }
      } 
      // FALLBACK: try to read from input (in case widgetState wasn't set for some reason)
      else if (sizeInput && sizeInput.value) {
        actualSizeCode = sizeInput.value;
        // Also update widgetState to keep them in sync
        widgetState.selectedSizeCode = actualSizeCode;
        console.log('[RamboWidget] Size from input (FALLBACK):', actualSizeCode);
      } 
      // NO SIZE SELECTED YET: this is OK if user hasn't selected a size yet
      else {
        console.log('[RamboWidget] ℹ️ No size selected yet - widgetState:', widgetState.selectedSizeCode || '(empty)', 'input:', sizeInput ? (sizeInput.value || '(empty)') : '(no input element)');
      }
      
      const engravingText = engravingInput ? engravingInput.value.trim() : '';
      const engravingFont = engravingFontSelect ? engravingFontSelect.value.trim() : '';
      
      // IMPORTANT: If we have a size in widgetState but actualSizeCode is empty, use widgetState
      // This prevents losing the size when saveSelectionsToStorage is called from other triggers
      if (!actualSizeCode && widgetState.selectedSizeCode) {
        actualSizeCode = widgetState.selectedSizeCode;
        console.log('[RamboWidget] 🔄 Recovered size from widgetState:', actualSizeCode);
      }
      
      // FALLBACK: Check for active size button in DOM (in case widgetState got cleared somehow)
      if (!actualSizeCode) {
        const activeSizeBtn = sectionEl ? sectionEl.querySelector('.tp-size-option.active') : 
                             document.querySelector('.tp-size-option.active');
        if (activeSizeBtn && activeSizeBtn.dataset.size) {
          actualSizeCode = activeSizeBtn.dataset.size;
          widgetState.selectedSizeCode = actualSizeCode;
          console.log('[RamboWidget] 🔄 Recovered size from active button:', actualSizeCode);
          
          // Also sync the input
          if (sizeInput) {
            sizeInput.value = actualSizeCode;
          }
        }
      }
      
      // FINAL FALLBACK: Preserve last saved size from localStorage if current is empty
      // This prevents losing the size when save is triggered from other fields (font/engraving)
      if (!actualSizeCode) {
        try {
          const lastSaved = localStorage.getItem(getStorageKey(productHandle));
          if (lastSaved) {
            const lastSelections = JSON.parse(lastSaved);
            if (lastSelections.sizeCode) {
              actualSizeCode = lastSelections.sizeCode;
              widgetState.selectedSizeCode = actualSizeCode;
              console.log('[RamboWidget] 🔄 Preserved last saved size from localStorage:', actualSizeCode);
              
              // Also sync the input and button
              if (sizeInput) {
                sizeInput.value = actualSizeCode;
              }
              const sizeBtn = sectionEl ? sectionEl.querySelector(`.tp-size-option[data-size="${actualSizeCode}"]`) : 
                             document.querySelector(`.tp-size-option[data-size="${actualSizeCode}"]`);
              if (sizeBtn) {
                sizeBtn.classList.add('active');
              }
            }
          }
        } catch (e) {
          // Silent fail - localStorage might not be available
        }
      }
      
      const selections = {
        styleIndex: widgetState.selectedStyleIndex,
        styleCode: widgetState.selectedStyleCode,
        styleLabel: widgetState.selectedStyleLabel,
        sizeCode: actualSizeCode,
        engravingText: engravingText,
        engravingFont: engravingFont,
        timestamp: Date.now()
      };

      console.log('[RamboWidget] 📦 About to save selections:', {
        sizeCode: actualSizeCode || '(EMPTY - no size selected)',
        widgetStateSize: widgetState.selectedSizeCode || '(empty)',
        sizeInputValue: sizeInput ? (sizeInput.value || '(empty)') : '(no input)',
        willSaveSize: !!actualSizeCode
      });

      localStorage.setItem(getStorageKey(productHandle), JSON.stringify(selections));
      
      // Also update URL with current selections
      console.log('[RamboWidget] 🔵 Calling updateUrlParams with styleCode:', selections.styleCode, 'styleIndex:', selections.styleIndex);
      updateUrlParams(
        selections.styleLabel, 
        selections.styleIndex, 
        selections.styleCode,
        selections.sizeCode,
        selections.engravingText,
        selections.engravingFont
      );
      
      console.log('[RamboWidget] ✅ SAVED:', {
        styleIndex: selections.styleIndex,
        styleLabel: selections.styleLabel,
        sizeCode: selections.sizeCode || '(empty)',
        engravingText: selections.engravingText ? (selections.engravingText.substring(0, 20) + (selections.engravingText.length > 20 ? '...' : '')) : '(empty)',
        engravingFont: selections.engravingFont || '(empty)',
        foundInput: !!engravingInput,
        foundFont: !!engravingFontSelect,
        foundSize: !!sizeInput,
        sizeInputValue: sizeInput ? sizeInput.value : '(no input)'
      });
    } catch (e) {
      console.error('[RamboWidget] ❌ Failed to save selections:', e);
    }
  }
  
  // Expose save function globally IMMEDIATELY so tp-product.js can call it
  window.saveRamboSelections = saveSelectionsToStorage;

  function restoreSelectionsFromStorage() {
    try {
      const productHandle = getProductHandle();
      const stored = localStorage.getItem(getStorageKey(productHandle));
      if (!stored) return null;

      const selections = JSON.parse(stored);
      // Only restore if saved within last 30 days
      const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 days in ms
      if (Date.now() - selections.timestamp > maxAge) {
        localStorage.removeItem(getStorageKey(productHandle));
        return null;
      }

      console.log('[RamboWidget] Restored selections from localStorage:', selections);
      return selections;
    } catch (e) {
      console.warn('[RamboWidget] Failed to restore selections:', e);
      return null;
    }
  }

  // Configuration - update RAMBO_API_BASE_URL to match your backend
  // You can set this via a Liquid variable in main-product.liquid if needed
  const RAMBO_API_BASE_URL = window.RAMBO_API_BASE_URL || 'https://rambo-ring-app-production.up.railway.app'; // Default to Railway production URL
  
  // Find the main product section (not header or other sections)
  function findProductSection() {
    // PREFER DESKTOP section first (has the ring style picker with proper IDs)
    let section = document.querySelector('#tp-desktop-product-wrapper') ||
                  document.querySelector('.tp-product-page') ||
                  document.querySelector('[id*="main-product"]:not([id*="mobile"])') ||
                  document.querySelector('[id*="main_product"]:not([id*="mobile"])');
    
    // If not found, look for any section with product-form or product-info (but not header/footer)
    if (!section) {
      const allSections = document.querySelectorAll('[id^="shopify-section-"]');
      for (const s of allSections) {
        // Skip header, footer, cart, and other non-product sections
        const sectionId = s.id.toLowerCase();
        if (sectionId.includes('header') || sectionId.includes('footer') || sectionId.includes('cart') || sectionId.includes('nav')) {
          continue;
        }
        // Check if this section has product-related content
        if (s.querySelector('product-form, .product-info, .product, [class*="product"]')) {
          section = s;
          break;
        }
      }
    }
    
    // Final fallback: find any section that's not header/footer and has a form with input[name="id"]
    if (!section) {
      const allSections = document.querySelectorAll('[id^="shopify-section-"]');
      for (const s of allSections) {
        const sectionId = s.id.toLowerCase();
        if (sectionId.includes('header') || sectionId.includes('footer') || sectionId.includes('cart') || sectionId.includes('nav')) {
          continue;
        }
        // Check if it has a product form
        if (s.querySelector('form input[name="id"]')) {
          section = s;
          break;
        }
      }
    }
    
    return section?.id || null;
  }
  
  // Get section ID - will be set when widget initializes
  let SECTION_ID = null;

  /**
   * Helper to clean ring size label (remove "US" and parentheses)
   */
  function cleanRingSizeLabel(label) {
    let cleanLabel = label || '';
    // Remove "US" (case-insensitive)
    cleanLabel = cleanLabel.replace(/\s*US\s*/gi, ' ').trim();
    // Remove parentheses and their contents
    cleanLabel = cleanLabel.replace(/\s*\([^)]*\)\s*/g, ' ').trim();
    // Extract just the number if there's still text
    const numberMatch = cleanLabel.match(/(\d+\.?\d*)/);
    if (numberMatch) {
      cleanLabel = numberMatch[1];
    }
    return cleanLabel;
  }

  /**
   * Initialize the widget
   */
  function initRamboRingWidget() {
    // Prevent duplicate initialization
    if (widgetInitialized) {
      console.log('[RamboWidget] Already initialized, skipping');
      return;
    }
    
    console.log('rambo-ring-widget: init starting...');
    // RESET widget state for each product page load (important for back/forward navigation)
    widgetState.selectedStyleIndex = -1;
    widgetState.selectedSizeCode = '';
    
    // Find the correct product section first
    SECTION_ID = findProductSection();
    if (!SECTION_ID) {
      return;
    }
    
    const productHandle = window.Shopify?.routes?.product || 
                         document.querySelector('[data-product-handle]')?.dataset?.productHandle ||
                         window.location.pathname.split('/products/')[1]?.split('?')[0];

    if (!productHandle) {
      return;
    }

    // Get ring config from Shopify metafield (set by Liquid in theme)
    // Liquid should set: window.RAMBO_RING_CONFIG = {{ product.metafields.rambo.ring_config.value | json }};
    let ringConfig = null;
    
    if (window.RAMBO_RING_CONFIG) {
      ringConfig = window.RAMBO_RING_CONFIG;
    } else if (window.Shopify?.analytics?.meta?.product?.metafields?.rambo?.ring_config) {
      try {
        ringConfig = JSON.parse(window.Shopify.analytics.meta.product.metafields.rambo.ring_config.value);
      } catch (e) {
        // Silent fail
      }
    }
    
    if (!ringConfig) {
      return;
    }

    // Process the ring config immediately (no async fetch needed)
    processRingConfig(ringConfig);
  }
  
  function processRingConfig(ringConfig) {
    // Hide native variant picker
    hideNativeVariantPicker();

    // Render custom widget
    renderRingWidget(ringConfig);

    // Override Add to Cart - wait a bit for form to be ready
    setTimeout(() => {
      overrideAddToCart(ringConfig);
    }, 100);
  }

  /**
   * Hide the native Shopify variant picker
   */
  function hideNativeVariantPicker() {
    const sectionEl = document.getElementById(SECTION_ID);
    if (!sectionEl) return;

    // Hide variant picker fieldsets (Prestige theme)
    const variantPickers = sectionEl.querySelectorAll('fieldset.variant-picker__option, .ProductForm__Option, .product-option');
    variantPickers.forEach(fieldset => {
      fieldset.style.display = 'none';
    });

    // Hide variant picker container if it exists
    const variantPickerContainer = sectionEl.querySelector('variant-picker');
    if (variantPickerContainer) {
      variantPickerContainer.style.display = 'none';
    }

    // Also hide any variant picker by class or ID (Prestige theme uses various classes)
    const variantPickerByClass = sectionEl.querySelectorAll('.variant-picker, .ProductForm__Variants, .product-single__variants');
    variantPickerByClass.forEach(el => {
      el.style.display = 'none';
    });

    // Hide any product form variant selectors
    const productForm = sectionEl.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const variantInputs = productForm.querySelectorAll('input[name="id"], select[name="id"]');
      variantInputs.forEach(input => {
        // Keep the input but hide its container
        const container = input.closest('.product-form__input, .variant-picker, fieldset, .ProductForm__Option');
        if (container) {
          container.style.display = 'none';
        }
      });
    }
  }

  /**
   * Render the custom ring widget (swatches + size dropdown)
   */
  function renderRingWidget(ringConfig) {
    const sectionEl = document.getElementById(SECTION_ID);
    if (!sectionEl) return;

    // Find the engraving area - we want to insert RIGHT BEFORE it
    // Wait a bit for it to be rendered if not found immediately
    let engravingArea = sectionEl.querySelector('.ring-engraving');
    
    // If not found, wait a bit and try again (engraving might be rendered after variant picker)
    if (!engravingArea) {
      // Try waiting for it to appear (max 1 second)
      let attempts = 0;
      const checkForEngraving = setInterval(() => {
        engravingArea = sectionEl.querySelector('.ring-engraving');
        if (engravingArea || attempts >= 20) { // 20 attempts * 50ms = 1 second max
          clearInterval(checkForEngraving);
          if (engravingArea) {
            insertWidget(ringConfig, engravingArea, sectionEl);
          } else {
            // Fallback if engraving never appears
            insertWidget(ringConfig, null, sectionEl);
          }
        }
        attempts++;
      }, 50);
      return; // Exit early, will be called again when engraving is found
    }
    
    // Engraving found immediately, insert widget
    insertWidget(ringConfig, engravingArea, sectionEl);
  }

  /**
   * Actually insert the widget into the DOM
   */
  function insertWidget(ringConfig, engravingArea, sectionEl) {
    // Check for existing swatches - desktop uses .ring-style-picker__swatch, mobile uses .tp-mobile-style
    const desktopSwatches = document.querySelectorAll('.ring-style-picker__swatch');
    const mobileSwatches = document.querySelectorAll('.tp-mobile-style');
    const totalSwatches = desktopSwatches.length + mobileSwatches.length;
    
    if (totalSwatches > 0) {
      console.log('[RamboWidget] Liquid swatches exist (desktop:' + desktopSwatches.length + ', mobile:' + mobileSwatches.length + ')');
      widgetInitialized = true;
      wireUpWidgetInteractions(ringConfig);
      return; // EXIT - never create JS widget when Liquid has swatches
    }
    
    // Only create widget if NO swatches exist anywhere
    console.log('[RamboWidget] No existing swatches, creating JS widget');
    
    // REMOVE existing widget first (important for re-initialization)
    const existingWidget = sectionEl.querySelector('.rambo-ring-widget');
    if (existingWidget) {
      existingWidget.remove();
    }
    
    // If engraving area exists, insert before it; otherwise fall back to variant picker or form
    let insertTarget = null;
    if (engravingArea) {
      insertTarget = engravingArea; // We'll use insertBefore on this
    } else {
      // Fallback: try to find variant picker or product form
      insertTarget = sectionEl.querySelector('variant-picker') || 
                     sectionEl.querySelector('.product-form') ||
                     sectionEl.querySelector('[data-product-form]') ||
                     sectionEl;
    }

                // Create widget container
                const widgetContainer = document.createElement('div');
                widgetContainer.className = 'rambo-ring-widget';
                widgetContainer.id = 'rambo-ring-widget-' + SECTION_ID;
                // Add inline style to ensure width matches engraving fields
                widgetContainer.style.width = '100%';

    // Build HTML
    let html = '<div class="rambo-ring-widget__container">';

    // Ring Styles Swatches
    html += '<div class="ring-style-picker" id="rambo-ring-style-picker">';
    html += '<div class="ring-style-picker__label">';
    html += 'Ring Styles <span class="ring-style-picker__required">*</span>';
    html += ' <span class="ring-style-picker__selected-name" id="rambo-ring-style-selected-name"></span>';
    html += '</div>';
    html += '<div class="ring-style-picker__swatches">';

    ringConfig.styles.forEach((style, index) => {
      const styleCodeMatch = (style.label || '').match(/^(\d{1,2})\s*[-–:]/);
      const styleCode = styleCodeMatch ? (styleCodeMatch[1].length === 1 ? '0' + styleCodeMatch[1] : styleCodeMatch[1]) : style.code || '';
      const swatchFn = (style.swatchImage || '').split('/').pop().split('?')[0].toLowerCase();
      
      // Add data-style-id and data-swatch-filename for unique identification
      html += `<button type="button" class="ring-style-picker__swatch" data-style-index="${index}" data-style-id="${style.id || ''}" data-style-label="${escapeHtml(style.label)}" data-style-code="${styleCode}" data-swatch-filename="${swatchFn}">`;
      
      let swatchSrc = style.swatchImage || '';
      
      if (swatchSrc) {
        // Check if this is an S3 URL (doesn't support resizing) or Shopify CDN (supports resizing)
        const isS3 = swatchSrc.includes('s3.') || swatchSrc.includes('amazonaws.com');
        
        if (isS3) {
          // S3 URLs: use as-is, images are already 600x600 high quality WebP
          html += `<img src="${escapeHtml(swatchSrc)}" alt="${escapeHtml(style.label)}" />`;
        } else {
          // Shopify CDN URLs: can use width params for resizing
          // Clean any existing width params first
          let cleanUrl = swatchSrc.replace(/([&?]width=\d+)/gi, '').replace(/([&?]format=\w+)/gi, '');
          // Remove trailing & or ?
          cleanUrl = cleanUrl.replace(/[&?]$/, '');
          
          // Build srcset with proper separators
          const sep = cleanUrl.includes('?') ? '&' : '?';
          const src1x = `${cleanUrl}${sep}width=100&format=webp`;
          const src2x = `${cleanUrl}${sep}width=200&format=webp`;
          const src3x = `${cleanUrl}${sep}width=300&format=webp`;
          
          html += `<img src="${escapeHtml(src3x)}" srcset="${escapeHtml(src1x)} 1x, ${escapeHtml(src2x)} 2x, ${escapeHtml(src3x)} 3x" alt="${escapeHtml(style.label)}" />`;
        }
      } else {
        html += `<span class="ring-style-picker__text">${escapeHtml(style.label)}</span>`;
      }
      html += '</button>';
    });

    html += '</div>'; // .ring-style-picker__swatches
    html += '<div class="field-error" id="rambo-ring-style-error" style="display: none; color: rgb(210, 0, 0); font-size: 0.8rem; margin-top: 0.5rem;">Please select a ring style.</div>';
    html += '</div>'; // .ring-style-picker

    // Ring Size Selector (using button grid)
    html += '<div class="tp-form-section">';
    html += '<div class="tp-form-label">';
    html += '<span>Ring Size <span class="required">*</span></span>';
    html += '<button type="button" class="tp-size-guide-link" onclick="document.getElementById(\'size-guide-modal\').classList.add(\'open\')">Size Guide</button>';
    html += '</div>';
    html += '<input type="hidden" name="properties[Ring Size]" value="" class="tp-size-input" id="rambo-ring-size-input" required>';
    html += '<div class="tp-size-options" id="rambo-ring-size-options">';
    // Size options will be populated dynamically by JavaScript
    html += '</div>';
    html += '<div class="field-error" id="rambo-ring-size-error" style="display: none; color: rgb(210, 0, 0); font-size: 0.8rem; margin-top: 0.5rem;">Please select a ring size.</div>';
    html += '</div>';

    html += '</div>'; // .rambo-ring-widget__container

    widgetContainer.innerHTML = html;

    // Insert widget - if we found engraving area, insert BEFORE it; otherwise use fallback
    if (engravingArea && engravingArea.parentNode) {
      engravingArea.parentNode.insertBefore(widgetContainer, engravingArea);
    } else if (insertTarget === sectionEl) {
      // Insert at the beginning if we can't find a better target
      insertTarget.insertBefore(widgetContainer, insertTarget.firstChild);
    } else if (insertTarget) {
      // Insert before the target element
      if (insertTarget.parentNode) {
        insertTarget.parentNode.insertBefore(widgetContainer, insertTarget);
      } else {
        insertTarget.insertBefore(widgetContainer, insertTarget.firstChild);
      }
    } else {
      // Last resort: append to section
      sectionEl.appendChild(widgetContainer);
    }

    // Store ring config data on widget for later use
    widgetContainer.dataset.ringConfig = JSON.stringify(ringConfig);

    // Wire up interactions
    widgetInitialized = true;
    wireUpWidgetInteractions(ringConfig);
  }

  /**
   * Wire up widget interactions (swatch clicks, size selection)
   */
  function wireUpWidgetInteractions(ringConfig) {
    console.log('rambo-ring-widget: wireUpWidgetInteractions starting...');
    
    // DIRECT APPROACH: Find desktop elements by their specific IDs/classes
    // Desktop section
    let sectionEl = document.querySelector('#tp-desktop-product-wrapper') ||
                    document.querySelector('.tp-product-page');
    
    // If still not found, fall back to SECTION_ID
    if (!sectionEl && SECTION_ID) {
      sectionEl = document.getElementById(SECTION_ID);
    }
    
    // Find style picker - MUST be in desktop section (has #rambo-ring-style-picker)
    let stylePicker = document.querySelector('#rambo-ring-style-picker');
    let sizeOptionsContainer = document.querySelector('#rambo-ring-size-options');
    
    // Fallback - search within desktop section
    if (!stylePicker && sectionEl) {
      stylePicker = sectionEl.querySelector('.rambo-ring-widget') ||
                    sectionEl.querySelector('[class*="ring-style"]');
    }

    if (!stylePicker) {
      console.error('[RamboWidget] stylePicker not found!');
      return;
    }
    
    if (!sizeOptionsContainer) {
      console.error('[RamboWidget] sizeOptionsContainer not found!');
      return;
    }
    
    console.log('[RamboWidget] ✅ Found stylePicker:', stylePicker.id || stylePicker.className);
    console.log('[RamboWidget] ✅ Found sizeOptions:', sizeOptionsContainer.id);

    // Filter logic REMOVED per user request
    // All swatches will be shown by default

    // Try to restore selections from URL parameters (when coming from cart "Update" button)
    // FIRST check for early-captured params (before theme could strip them)
    const earlyParams = window.__tpStyleParams || {};
    const urlParams = new URLSearchParams(window.location.search);
    
    // Use early-captured params if available, otherwise fall back to current URL
    const urlStyleIndex = earlyParams.styleIndex !== null && earlyParams.styleIndex !== undefined 
      ? String(earlyParams.styleIndex) 
      : urlParams.get('style_index');
    console.log('[RamboWidget] URL params - style_index:', urlStyleIndex, 'early captured:', earlyParams.captured, 'current URL:', window.location.search);
    const urlRingSku = urlParams.get('ring_sku');
    const urlRingStyle = earlyParams.ringStyle || urlParams.get('ring_style');
    const urlRingSize = urlParams.get('ring_size');
    const urlStyleId = urlParams.get('style_id') || urlParams.get('prefilter_style_id');
    const urlSwatchFn = urlParams.get('swatch_filename');
    
    // Handle prefilter_style_code (from Collection Page) -> HIGHEST PRIORITY - has exact style number
    // Format: "STYLE04" or "STYLE_04" or just "04" -> style index 3 (0-based)
    // Also check _ss param where Shopify sometimes encodes our params
    let prefilterStyleCode = earlyParams.styleCode || urlParams.get('prefilter_style_code');
    
    // If not found, check if it's encoded in Shopify's _ss tracking param
    if (!prefilterStyleCode) {
      const ssParam = urlParams.get('_ss');
      if (ssParam) {
        const ssMatch = ssParam.match(/prefilter_style_code[=:]([A-Z_]*\d+)/i);
        if (ssMatch) {
          prefilterStyleCode = ssMatch[1];
          console.log('[RamboWidget] Extracted prefilter_style_code from _ss:', prefilterStyleCode);
        }
      }
    }
    console.log('[RamboWidget] prefilter_style_code:', prefilterStyleCode);
    // Store the parsed style code index to use as final authority later
    let prefilterStyleIndex = -1;
    if (prefilterStyleCode) {
        console.log('[RamboWidget] Looking for style code:', prefilterStyleCode);
        
        // Extract the style number from the code (STYLE01 -> 01, STYLE_05 -> 05)
        const codeMatch = prefilterStyleCode.match(/STYLE[_\-]?(\d+)/i);
        const targetNum = codeMatch ? codeMatch[1].padStart(2, '0') : null;
        console.log('[RamboWidget] Target style number:', targetNum);
        
        if (targetNum) {
            // METHOD 1: Check DOM swatches by data-swatch-filename (most reliable - contains style number)
            const domSwatches = document.querySelectorAll('.ring-style-picker__swatch');
            console.log('[RamboWidget] Found', domSwatches.length, 'DOM swatches');
            
            for (let i = 0; i < domSwatches.length; i++) {
                const swatch = domSwatches[i];
                const filename = (swatch.dataset.swatchFilename || '').toLowerCase();
                const styleCode = (swatch.dataset.styleCode || '').toUpperCase();
                
                // Check filename for style number (e.g., "style01-swatch.webp")
                const filenameMatch = filename.match(/style[_\-]?(\d+)/i);
                if (filenameMatch && filenameMatch[1].padStart(2, '0') === targetNum) {
                    prefilterStyleIndex = parseInt(swatch.dataset.styleIndex, 10);
                    console.log('[RamboWidget] Found by filename at index:', prefilterStyleIndex, 'filename:', filename);
                    break;
                }
                
                // Also check data-style-code
                const codeNumMatch = styleCode.match(/STYLE[_\-]?(\d+)/i);
                if (codeNumMatch && codeNumMatch[1].padStart(2, '0') === targetNum) {
                    prefilterStyleIndex = parseInt(swatch.dataset.styleIndex, 10);
                    console.log('[RamboWidget] Found by styleCode at index:', prefilterStyleIndex, 'code:', styleCode);
                    break;
                }
            }
            
            // METHOD 2: Check ringConfig.styles array
            if (prefilterStyleIndex < 0) {
                for (let i = 0; i < ringConfig.styles.length; i++) {
                    const style = ringConfig.styles[i];
                    const styleCode = (style.code || '').toUpperCase();
                    const swatchImg = (style.swatchImage || '').toLowerCase();
                    
                    // Check code
                    const codeNum = styleCode.match(/STYLE[_\-]?(\d+)/i);
                    if (codeNum && codeNum[1].padStart(2, '0') === targetNum) {
                        prefilterStyleIndex = i;
                        console.log('[RamboWidget] Found by ringConfig code at index:', i);
                        break;
                    }
                    
                    // Check swatchImage filename
                    const imgNum = swatchImg.match(/style[_\-]?(\d+)/i);
                    if (imgNum && imgNum[1].padStart(2, '0') === targetNum) {
                        prefilterStyleIndex = i;
                        console.log('[RamboWidget] Found by ringConfig swatchImage at index:', i);
                        break;
                    }
                }
            }
        }
        
        if (prefilterStyleIndex >= 0 && prefilterStyleIndex < ringConfig.styles.length) {
            widgetState.selectedStyleIndex = prefilterStyleIndex;
            widgetState.selectedStyleLabel = ringConfig.styles[prefilterStyleIndex].label || '';
            widgetState.selectedStyleCode = ringConfig.styles[prefilterStyleIndex].code || '';
            console.log('[RamboWidget] SUCCESS! Set selectedStyleIndex to:', prefilterStyleIndex, 'label:', widgetState.selectedStyleLabel);
        } else {
            console.log('[RamboWidget] FAILED to find style', targetNum, 'in', ringConfig.styles.length, 'styles');
        }
    }
    
    // Handle prefilter_color (from Collection Page) -> Auto-select but don't hide
    // logic: if prefilter_color is present and ring_style (cart edit) is NOT, use prefilter to set initial selection
    if (!urlRingStyle && widgetState.selectedStyleIndex < 0) { 
        const prefilterColor = earlyParams.prefilterColor || urlParams.get('prefilter_color');
        if (prefilterColor) {
            // Find match in styles
            const matchIndex = ringConfig.styles.findIndex(style => {
                const filterVal = prefilterColor.toLowerCase();
                const styleColor = (style.color || '').toLowerCase();
                const styleLabel = (style.label || '').toLowerCase();
                const styleShape = (style.shape || '').toLowerCase();
                
                return styleLabel === filterVal || 
                       styleColor === filterVal || 
                       styleShape === filterVal ||
                       (filterVal.length > 2 && styleLabel.includes(filterVal));
            });

            if (matchIndex !== -1) {
                 widgetState.selectedStyleIndex = matchIndex;
                 widgetState.selectedStyleLabel = ringConfig.styles[matchIndex].label || '';
                 widgetState.selectedStyleCode = ringConfig.styles[matchIndex].code || '';
            }
        }
    }
    
    // Use shared state
    // widgetState.selectedStyleIndex and widgetState.selectedSizeCode are defined at top level
    
    // If URL has style index, SKU, or ring style/size, try to restore them
    if (urlStyleIndex || urlRingSku || urlRingStyle || urlRingSize || urlStyleId || urlSwatchFn) {
      let matchingStyleIndex = -1;
      
      // PRIORITY 1: Use exact style_index if provided (most reliable)
      if (urlStyleIndex) {
        const idx = parseInt(urlStyleIndex, 10);
        console.log('[RamboWidget] Parsed style_index:', idx, 'from URL param:', urlStyleIndex);
        if (!isNaN(idx) && idx >= 0 && idx < ringConfig.styles.length) {
          matchingStyleIndex = idx;
          console.log('[RamboWidget] style_index valid, set matchingStyleIndex to:', idx);
        } else {
          console.log('[RamboWidget] style_index invalid - isNaN:', isNaN(idx), 'outOfRange:', idx < 0 || idx >= ringConfig.styles.length);
        }
      }
      
      // PRIORITY 2: Match by unique style_id
      if (matchingStyleIndex < 0 && urlStyleId) {
        matchingStyleIndex = ringConfig.styles.findIndex(style => String(style.id) === String(urlStyleId));
      }

      // PRIORITY 3: Match by swatch filename
      if (matchingStyleIndex < 0 && urlSwatchFn) {
        const targetFn = urlSwatchFn.toLowerCase();
        matchingStyleIndex = ringConfig.styles.findIndex(style => {
          const fn = (style.swatchImage || '').split('/').pop().split('?')[0].toLowerCase();
          return fn === targetFn;
        });
      }
      
      // PRIORITY 4: Match by SKU
      if (matchingStyleIndex < 0 && urlRingSku) {
        matchingStyleIndex = ringConfig.styles.findIndex(style => {
          return style.variants && style.variants.some(v => v.sku === urlRingSku);
        });
      }
      
      // PRIORITY 5: Fall back to label matching
      if (matchingStyleIndex < 0 && urlRingStyle) {
        matchingStyleIndex = ringConfig.styles.findIndex(style => {
          const cleanUrlStyle = urlRingStyle.replace(/\s+Tungsten\s*/gi, ' ').replace(/^\d{1,2}\s*[-–:]\s*/, '').trim().toLowerCase();
          const cleanStyleLabel = style.label.replace(/\s+Tungsten\s*/gi, ' ').replace(/^\d{1,2}\s*[-–:]\s*/, '').trim().toLowerCase();
          return cleanStyleLabel === cleanUrlStyle || style.label.toLowerCase() === urlRingStyle.toLowerCase();
        });
      }
      
      if (matchingStyleIndex >= 0) {
        // BUT: prefilter_style_code has FINAL authority since it has exact style number
        // Only use matchingStyleIndex if prefilterStyleIndex wasn't set
        if (prefilterStyleIndex >= 0 && prefilterStyleIndex !== matchingStyleIndex) {
          console.log('[RamboWidget] prefilter_style_code overrides style_index:', prefilterStyleIndex, 'vs', matchingStyleIndex);
          matchingStyleIndex = prefilterStyleIndex;
        }
        
        console.log('[RamboWidget] Setting selectedStyleIndex to:', matchingStyleIndex);
        widgetState.selectedStyleIndex = matchingStyleIndex;
        widgetState.selectedStyleLabel = ringConfig.styles[matchingStyleIndex].label || '';
        widgetState.selectedStyleCode = ringConfig.styles[matchingStyleIndex].code || '';
        // Update size dropdown for this style
        updateSizeDropdown(ringConfig.styles[matchingStyleIndex].variants, ringConfig, matchingStyleIndex);
        
        // Find matching size and select it
        if (urlRingSize) {
          const matchingVariant = ringConfig.styles[matchingStyleIndex].variants.find(v => {
            // Match by sizeLabel or sizeCode
            return v.sizeLabel === urlRingSize || v.sizeCode === urlRingSize;
          });
          
          if (matchingVariant) {
            widgetState.selectedSizeCode = matchingVariant.sizeCode;
            console.log('[RamboWidget] Restoring size from URL:', matchingVariant.sizeCode);
            setTimeout(() => {
              const btn = sectionEl.querySelector(`.tp-size-option[data-size="${widgetState.selectedSizeCode}"]`);
              if (btn) {
                btn.classList.add('active');
                const input = sectionEl.querySelector('#rambo-ring-size-input');
                if (input) {
                  input.value = widgetState.selectedSizeCode;
                  console.log('[RamboWidget] Size input set to:', input.value);
                } else {
                  console.warn('[RamboWidget] Size input not found when restoring from URL');
                }
              } else {
                console.warn('[RamboWidget] Size button not found for:', widgetState.selectedSizeCode);
              }
              // Update Add to Cart button state after size is restored from URL
              if (typeof window.updateStickyBar === 'function') {
                setTimeout(() => {
                  window.updateStickyBar();
                  console.log('[RamboWidget] ✅ Updated Add to Cart button state after URL size restoration');
                }, 50);
              }
              
              // Save to localStorage after restoring from URL
              setTimeout(() => {
                saveSelectionsToStorage();
              }, 50);
            }, 100);
          } else {
            console.warn('[RamboWidget] No matching variant found for URL size:', urlRingSize);
          }
        }
        
        // Save to localStorage after restoring from URL params (so they persist on refresh)
        setTimeout(() => {
          saveSelectionsToStorage();
          
          // Update Add to Cart button state after all URL params are restored
          if (typeof window.updateStickyBar === 'function') {
            setTimeout(() => {
              window.updateStickyBar();
              console.log('[RamboWidget] ✅ Updated Add to Cart button state after URL param restoration');
            }, 100);
          }
        }, 500);
      }
    }

    // Check if we have *any* style-related URL parameters (declare BEFORE using it)
    const hasUrlStyleParams = urlStyleIndex || urlParams.get('ring_sku') || urlRingStyle || prefilterStyleCode || urlParams.get('prefilter_color') || urlParams.get('style_id') || urlParams.get('prefilter_style_id') || urlParams.get('swatch_filename');

    // RESTORE RING STYLE FROM LOCALSTORAGE (only if no URL params)
    // URL params take priority, but if none were provided, restore saved style
    // Declare at function scope so it's accessible later when clicking swatches
    let savedStyleIndexToRestore = null;
    let savedStyleCode = null;
    let savedStyleLabel = null;
    const savedSelections = restoreSelectionsFromStorage();
    if (savedSelections) {
      // Restore ring style first (if no URL params)
      // Store the saved style index to restore after swatches are wired up
      if (!hasUrlStyleParams && widgetState.selectedStyleIndex < 0 && savedSelections.styleIndex >= 0 && savedSelections.styleIndex < ringConfig.styles.length) {
        savedStyleIndexToRestore = savedSelections.styleIndex;
        savedStyleCode = savedSelections.styleCode || ringConfig.styles[savedSelections.styleIndex].code || '';
        savedStyleLabel = savedSelections.styleLabel || ringConfig.styles[savedSelections.styleIndex].label || '';
        
        widgetState.selectedStyleIndex = savedStyleIndexToRestore;
        widgetState.selectedStyleCode = savedStyleCode;
        widgetState.selectedStyleLabel = savedStyleLabel;
        
        console.log('[RamboWidget] ✅ Restored ring style state from localStorage:', savedStyleIndexToRestore, savedStyleLabel);
        console.log('[RamboWidget] ℹ️ Will click swatch after wiring is complete');
      }
      
      console.log('[RamboWidget] 📦 Restoring size, engraving, and font from localStorage');
      
      // Wait for size dropdown to be ready (it depends on style being selected first)
      // If a style was selected from URL or localStorage, wait a bit for the dropdown to update
      // If no style was selected, we need to wait for default style to be set
      const restoreDelay = hasUrlStyleParams || widgetState.selectedStyleIndex >= 0 ? 500 : 800;
      
      // Restore size (ALWAYS, regardless of URL params or style)
      if (savedSelections.sizeCode) {
        setTimeout(() => {
          widgetState.selectedSizeCode = savedSelections.sizeCode;
          const sizeBtn = sectionEl.querySelector(`.tp-size-option[data-size="${savedSelections.sizeCode}"]`);
          if (sizeBtn) {
            sizeBtn.classList.add('active');
            const sizeInput = sectionEl.querySelector('#rambo-ring-size-input');
            if (sizeInput) {
              sizeInput.value = savedSelections.sizeCode;
              console.log('[RamboWidget] ✅ Restored size from localStorage:', savedSelections.sizeCode);
            }
          } else {
            console.warn('[RamboWidget] ⚠️ Size button not found for:', savedSelections.sizeCode, '- will retry after dropdown updates');
            // Retry after a longer delay in case dropdown wasn't ready
            setTimeout(() => {
              const retrySizeBtn = sectionEl.querySelector(`.tp-size-option[data-size="${savedSelections.sizeCode}"]`);
              if (retrySizeBtn) {
                retrySizeBtn.classList.add('active');
                const retrySizeInput = sectionEl.querySelector('#rambo-ring-size-input');
                if (retrySizeInput) {
                  retrySizeInput.value = savedSelections.sizeCode;
                  widgetState.selectedSizeCode = savedSelections.sizeCode;
                  console.log('[RamboWidget] ✅ Retry: Restored size from localStorage:', savedSelections.sizeCode);
                }
              }
            }, 500);
          }
          
          // Update Add to Cart button state after size is restored
          if (typeof window.updateStickyBar === 'function') {
            setTimeout(() => {
              window.updateStickyBar();
              console.log('[RamboWidget] ✅ Updated Add to Cart button state after size restoration');
            }, 100);
          }
        }, restoreDelay);
      }
      
      // Restore engraving and font (ALWAYS, regardless of URL params or style)
      setTimeout(() => {
        const engravingInput = sectionEl.querySelector('.tp-engraving-input');
        const engravingFontSelect = sectionEl.querySelector('#engraving-font-input') || 
                                    sectionEl.querySelector('select[id*="engraving-font"]');
        
        if (engravingInput && savedSelections.engravingText) {
          engravingInput.value = savedSelections.engravingText;
          console.log('[RamboWidget] ✅ Restored engraving text from localStorage:', savedSelections.engravingText);
          
          // Update preview text content and visibility
          const previewText = sectionEl.querySelector('.tp-preview-text') || 
                             document.querySelector('.tp-preview-text') ||
                             document.querySelector('.tp-engraving-preview-text') ||
                             document.querySelector('[data-preview-text]');
          const previewPlaceholder = sectionEl.querySelector('.tp-preview-placeholder') || 
                                    document.querySelector('.tp-preview-placeholder') ||
                                    document.querySelector('[data-preview-placeholder]');
          
          if (previewText && savedSelections.engravingText.trim()) {
            previewText.textContent = savedSelections.engravingText;
            previewText.style.display = 'flex';
            console.log('[RamboWidget] ✅ Updated preview text content');
            
            // Apply font if it's already been restored or selected
            const currentFont = savedSelections.engravingFont;
            if (currentFont) {
              const fontOptions = document.querySelectorAll('.tp-select-option');
              let matchingFontOption = null;
              fontOptions.forEach(option => {
                if (option.dataset.value === currentFont) {
                  matchingFontOption = option;
                }
              });
              
              if (matchingFontOption) {
                const fontFamily = matchingFontOption.dataset.font || matchingFontOption.style.fontFamily || '';
                if (fontFamily) {
                  previewText.style.fontFamily = fontFamily;
                  console.log('[RamboWidget] ✅ Applied saved font to preview:', fontFamily);
                }
              }
            }
            
            // Try to trigger auto-sizing if the function exists
            if (typeof window.autoSizePreviewText === 'function') {
              window.autoSizePreviewText(previewText, savedSelections.engravingText);
            }
          }
          
          if (previewPlaceholder) {
            previewPlaceholder.style.display = savedSelections.engravingText.trim() ? 'none' : 'flex';
          }
          
          // Update character counter if it exists
          const charCount = sectionEl.querySelector('.tp-char-count') || 
                           document.querySelector('.tp-char-count') ||
                           document.querySelector('[data-char-count]');
          if (charCount) {
            charCount.textContent = savedSelections.engravingText.length;
          }
          
          // Trigger input event to update any listeners (this should also trigger preview update)
          engravingInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
        
        if (savedSelections.engravingFont) {
          // Update hidden input if it exists
          if (engravingFontSelect) {
            engravingFontSelect.value = savedSelections.engravingFont;
            console.log('[RamboWidget] ✅ Set hidden input value to:', savedSelections.engravingFont);
            
            // Trigger change event to update any listeners
            engravingFontSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
          
          // Update custom font dropdown visual display
          const fontSelected = document.getElementById('font-selected');
          const fontOptions = document.querySelectorAll('.tp-select-option');
          
          if (fontSelected && fontOptions.length > 0) {
            // Find the matching option by data-value attribute
            let matchingOption = null;
            fontOptions.forEach(option => {
              if (option.dataset.value === savedSelections.engravingFont) {
                matchingOption = option;
              }
            });
            
            if (matchingOption) {
              // Update active state on all options
              fontOptions.forEach(opt => opt.classList.remove('active'));
              matchingOption.classList.add('active');
              
              // Update the trigger display
              const fontFamily = matchingOption.dataset.font || matchingOption.style.fontFamily || '';
              fontSelected.textContent = savedSelections.engravingFont;
              fontSelected.style.fontFamily = fontFamily;
              
              // Update preview text with font family
              const previewText = sectionEl.querySelector('.tp-preview-text') || 
                                 document.querySelector('.tp-preview-text') ||
                                 document.querySelector('.tp-engraving-preview-text') ||
                                 document.querySelector('[data-preview-text]');
              if (previewText && fontFamily) {
                previewText.style.fontFamily = fontFamily;
                console.log('[RamboWidget] ✅ Applied font to preview:', fontFamily);
                
                // If preview has text, trigger auto-sizing again with the new font
                if (previewText.textContent && typeof window.autoSizePreviewText === 'function') {
                  window.autoSizePreviewText(previewText, previewText.textContent);
                }
              }
              
              console.log('[RamboWidget] ✅ Updated font dropdown visual display:', savedSelections.engravingFont, 'fontFamily:', fontFamily);
            } else {
              console.warn('[RamboWidget] ⚠️ Font option not found for:', savedSelections.engravingFont);
              // Fallback: just update the text and try to apply the font
              fontSelected.textContent = savedSelections.engravingFont;
            }
          } else if (fontSelected) {
            // If no options found but we have the selected element, just update the text
            fontSelected.textContent = savedSelections.engravingFont;
            console.log('[RamboWidget] ✅ Updated font-selected text (no options found):', savedSelections.engravingFont);
          } else {
            console.warn('[RamboWidget] ⚠️ Font dropdown elements not found - will retry');
            // Retry after a delay in case the dropdown hasn't initialized yet
            setTimeout(() => {
              const retryFontSelected = document.getElementById('font-selected');
              const retryFontOptions = document.querySelectorAll('.tp-select-option');
              
              if (retryFontSelected && retryFontOptions.length > 0) {
                let retryMatchingOption = null;
                retryFontOptions.forEach(option => {
                  if (option.dataset.value === savedSelections.engravingFont) {
                    retryMatchingOption = option;
                  }
                });
                
                if (retryMatchingOption) {
                  retryFontOptions.forEach(opt => opt.classList.remove('active'));
                  retryMatchingOption.classList.add('active');
                  
                  const fontFamily = retryMatchingOption.dataset.font || retryMatchingOption.style.fontFamily || '';
                  retryFontSelected.textContent = savedSelections.engravingFont;
                  retryFontSelected.style.fontFamily = fontFamily;
                  
                  // Update preview text with font family
                  const retryPreviewText = sectionEl.querySelector('.tp-preview-text') || 
                                          document.querySelector('.tp-preview-text') ||
                                          document.querySelector('.tp-engraving-preview-text') ||
                                          document.querySelector('[data-preview-text]');
                  if (retryPreviewText && fontFamily) {
                    retryPreviewText.style.fontFamily = fontFamily;
                    console.log('[RamboWidget] ✅ Retry: Applied font to preview:', fontFamily);
                    
                    // If preview has text, trigger auto-sizing again with the new font
                    if (retryPreviewText.textContent && typeof window.autoSizePreviewText === 'function') {
                      window.autoSizePreviewText(retryPreviewText, retryPreviewText.textContent);
                    }
                  }
                  
                  console.log('[RamboWidget] ✅ Retry: Updated font dropdown visual display:', savedSelections.engravingFont);
                }
              }
            }, 500);
          }
        }
        
        // Final step: Ensure preview is fully updated with both text and font
        setTimeout(() => {
          const finalPreviewText = sectionEl.querySelector('.tp-preview-text') || 
                                   document.querySelector('.tp-preview-text') ||
                                   document.querySelector('.tp-engraving-preview-text') ||
                                   document.querySelector('[data-preview-text]');
          
          if (finalPreviewText && savedSelections.engravingText && savedSelections.engravingText.trim()) {
            // Ensure text is set
            if (finalPreviewText.textContent !== savedSelections.engravingText) {
              finalPreviewText.textContent = savedSelections.engravingText;
              finalPreviewText.style.display = 'flex';
            }
            
            // Ensure font is applied
            if (savedSelections.engravingFont) {
              const finalFontOptions = document.querySelectorAll('.tp-select-option');
              let finalMatchingFontOption = null;
              finalFontOptions.forEach(option => {
                if (option.dataset.value === savedSelections.engravingFont) {
                  finalMatchingFontOption = option;
                }
              });
              
              if (finalMatchingFontOption) {
                const finalFontFamily = finalMatchingFontOption.dataset.font || finalMatchingFontOption.style.fontFamily || '';
                if (finalFontFamily && finalPreviewText.style.fontFamily !== finalFontFamily) {
                  finalPreviewText.style.fontFamily = finalFontFamily;
                  console.log('[RamboWidget] ✅ Final: Applied font to preview:', finalFontFamily);
                }
              }
            }
            
            // Trigger auto-sizing one more time
            if (typeof window.autoSizePreviewText === 'function') {
              window.autoSizePreviewText(finalPreviewText, savedSelections.engravingText);
            }
          }
        }, 300);
        
        // Update Add to Cart button state after engraving/font are restored
        if (typeof window.updateStickyBar === 'function') {
          setTimeout(() => {
            window.updateStickyBar();
            console.log('[RamboWidget] ✅ Updated Add to Cart button state after engraving/font restoration');
          }, 100);
        }
        
        // Save after restoration to update timestamp
        saveSelectionsToStorage();
      }, restoreDelay + 200);
    } else {
      console.log('[RamboWidget] ℹ️ No saved selections found in localStorage');
    }

    // Get the selected name display element
    const selectedNameEl = sectionEl.querySelector('#rambo-ring-style-selected-name');
    
    // Function to update the selected style name display
    const updateSelectedStyleName = (styleIndex) => {
      if (selectedNameEl && styleIndex >= 0 && styleIndex < ringConfig.styles.length) {
        const style = ringConfig.styles[styleIndex];
        selectedNameEl.textContent = style.label;
      } else if (selectedNameEl) {
        selectedNameEl.textContent = '';
      }
    };

    // Style swatch click handlers - search BOTH inside stylePicker AND entire document as fallback
    let swatches = stylePicker.querySelectorAll('.ring-style-picker__swatch');
    
    // FALLBACK: If no swatches in stylePicker, search entire document (desktop swatches only)
    if (swatches.length === 0) {
      console.log('[RamboWidget] ⚠️ No swatches in stylePicker, searching document...');
      swatches = document.querySelectorAll('#rambo-ring-style-picker .ring-style-picker__swatch, .tp-product-page .ring-style-picker__swatch');
    }
    
    console.log('[RamboWidget] 🎯 Found ' + swatches.length + ' swatches to wire up');
    
    if (swatches.length === 0) {
      console.error('[RamboWidget] ❌ NO SWATCHES FOUND! Cannot attach click handlers.');
      return;
    }

    // hasUrlStyleParams is already declared above

    swatches.forEach((swatch, index) => {
      // ALWAYS attach fresh listeners (remove old flag first)
      // This ensures handlers work even after cache/reload
      delete swatch.dataset.listenerAttached;
      swatch.dataset.listenerAttached = 'true';

      // Set active state based on widgetState - will be handled by forceSetVisualState after all swatches are wired
      
      // Create a named function so we can remove it if needed
      const clickHandler = function(e) {
        console.log('[RamboWidget] 🖱️ SWATCH CLICKED! Index:', index);
        
        // Stop propagation and prevent default to ensure our handler runs first
        e.stopPropagation();
        e.stopImmediatePropagation();
        e.preventDefault();
        
        // Get the actual clicked swatch (use e.currentTarget to get the element with the listener)
        const clickedSwatch = e.currentTarget || e.target.closest('.ring-style-picker__swatch') || swatch;
        const clickedIndex = parseInt(clickedSwatch.dataset.styleIndex) || index;
        
        console.log('[RamboWidget] 🖱️ Processing click for style index:', clickedIndex);
        
        console.log('rambo-ring-widget: Swatch click detected at index ' + clickedIndex);
        console.log('[RamboWidget] 🔵🔵🔵 CLICK EVENT - Swatch element:', clickedSwatch, 'Index:', clickedIndex, 'Original index:', index);
        
        // Update widgetState FIRST
        widgetState.selectedStyleIndex = clickedIndex;
        widgetState.userManuallySelectedStyle = true;
        widgetState.lastManualStyleIndex = clickedIndex;
        
        // FORCE visual state using centralized function - IMMEDIATELY, SYNCHRONOUSLY
        forceSetVisualState();
        
        // Also set it again after a tiny delay to catch any async clearing
        setTimeout(() => {
          forceSetVisualState();
        }, 0);
        console.log('[RamboWidget] ✅✅✅ USER MANUALLY SELECTED STYLE - Index:', index, 'Code:', widgetState.selectedStyleCode, 'Flag set to prevent restoration override');

        // Clear error
        const errorEl = document.getElementById('rambo-ring-style-error');
        if (errorEl) errorEl.style.display = 'none';

        const previousSizeCode = widgetState.selectedSizeCode; // Capture current size
        widgetState.selectedStyleIndex = clickedIndex;
        widgetState.selectedStyleLabel = ringConfig.styles[clickedIndex].label || '';
        // Get style code from ringConfig, or fallback to swatch data attribute, ensure uppercase
        let styleCode = ringConfig.styles[clickedIndex].code || clickedSwatch.dataset.styleCode || '';
        if (!styleCode && clickedSwatch.dataset.swatchFilename) {
          const match = clickedSwatch.dataset.swatchFilename.match(/style[_\-]?(\d+)/i);
          if (match) {
            styleCode = `STYLE${match[1].padStart(2, '0')}`;
          }
        }
        // Final fallback: generate from index (0-based, so add 1 and pad)
        if (!styleCode) {
          styleCode = `STYLE${String(clickedIndex + 1).padStart(2, '0')}`;
        }
        widgetState.selectedStyleCode = styleCode.toUpperCase();
        
        console.log('[RamboWidget] 🔵 Style swatch clicked - index:', clickedIndex, 'code:', widgetState.selectedStyleCode, 'label:', widgetState.selectedStyleLabel);
        console.log('[RamboWidget] 🔵 Style code sources:', {
          fromRingConfig: ringConfig.styles[clickedIndex].code,
          fromSwatchData: clickedSwatch.dataset.styleCode,
          fromFilename: clickedSwatch.dataset.swatchFilename,
          finalCode: widgetState.selectedStyleCode
        });
        
        // Verify the data attribute is set correctly
        if (clickedSwatch.dataset.styleIndex !== String(clickedIndex)) {
          clickedSwatch.dataset.styleIndex = String(clickedIndex);
        }
        
        // Update selected style name display
        updateSelectedStyleName(clickedIndex);
        
        // Verify active state is set (with retry if needed)
        setTimeout(() => {
          if (!clickedSwatch.classList.contains('ring-style-picker__swatch--active')) {
            console.warn('[RamboWidget] ⚠️ Active class not set, forcing it...');
            clickedSwatch.classList.add('ring-style-picker__swatch--active');
          }
          // Double-check all others are inactive - CSS handles visuals
          swatches.forEach((s, idx) => {
            if (idx !== clickedIndex) {
              s.classList.remove('ring-style-picker__swatch--active');
              s.removeAttribute('data-active');
              s.style.outline = 'none';
            }
          });
          console.log('[RamboWidget] ✅ Verified active state - swatch at index', clickedIndex, 'is active:', clickedSwatch.classList.contains('ring-style-picker__swatch--active'));
        }, 10);
        
        // Watch for active class being removed and restore it
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
              if (!clickedSwatch.classList.contains('ring-style-picker__swatch--active') && widgetState.lastManualStyleIndex === clickedIndex) {
                console.warn('[RamboWidget] ⚠️ Active class was removed from manually selected swatch! Restoring...');
                clickedSwatch.classList.add('ring-style-picker__swatch--active');
                // Ensure others are inactive - CSS handles visuals
                swatches.forEach((s, idx) => {
                  if (idx !== clickedIndex) {
                    s.classList.remove('ring-style-picker__swatch--active');
                    s.removeAttribute('data-active');
                    s.style.outline = 'none';
                  }
                });
              }
            }
          });
        });
        
        // Start observing the swatch for class and style changes
        observer.observe(clickedSwatch, {
          attributes: true,
          attributeFilter: ['class', 'style']
        });
        
        // Store observer so we can disconnect it later if needed
        clickedSwatch._activeObserver = observer;
        
        // Also use a periodic check for the first 2 seconds after click to ensure active state persists
        let checkCount = 0;
        const maxChecks = 20; // Check 20 times over 2 seconds
        const activeStateChecker = setInterval(() => {
          checkCount++;
          if (checkCount >= maxChecks || widgetState.lastManualStyleIndex !== clickedIndex) {
            clearInterval(activeStateChecker);
            return;
          }
          
          // If this swatch should be active but isn't, restore it
          if (!clickedSwatch.classList.contains('ring-style-picker__swatch--active') && widgetState.lastManualStyleIndex === clickedIndex) {
            console.warn('[RamboWidget] ⚠️ Active class missing during check, restoring...');
            swatches.forEach(s => {
              if (s !== clickedSwatch) {
                s.classList.remove('ring-style-picker__swatch--active');
                s.removeAttribute('data-active');
                s.style.outline = 'none';
              }
            });
            clickedSwatch.classList.add('ring-style-picker__swatch--active');
            clickedSwatch.setAttribute('data-active', 'true');
          }
        }, 100); // Check every 100ms

        // Update size dropdown options (using popover structure)
        updateSizeDropdown(ringConfig.styles[clickedIndex].variants, ringConfig, clickedIndex);
        
        // Try to preserve size selection
        let preservedSize = false;
        if (previousSizeCode) {
           // Find equivalent variant in new style
           // Assuming sizeCode is consistent across styles (e.g. "10.5")
           const newStyleVariants = ringConfig.styles[clickedIndex].variants;
           const matchingVariant = newStyleVariants.find(v => v.sizeCode === previousSizeCode);
           
           if (matchingVariant) {
             // Found match, re-select it
             widgetState.selectedSizeCode = previousSizeCode;
             preservedSize = true;
             
             // Update UI to reflect selection
             // Use slight delay to ensure dropdown DOM is ready
             setTimeout(() => {
                const btn = sectionEl.querySelector(`.tp-size-option[data-size="${widgetState.selectedSizeCode}"]`);
                if (btn) {
                  btn.classList.add('active');
                  const input = sectionEl.querySelector('#rambo-ring-size-input');
                  if (input) input.value = widgetState.selectedSizeCode;
                }
                // Update price
                updatePrice(ringConfig, clickedIndex, widgetState.selectedSizeCode);
             }, 10);
           }
        }
        
        if (!preservedSize) {
           widgetState.selectedSizeCode = '';
           // Clear hidden input
           const input = sectionEl.querySelector('#rambo-ring-size-input');
           if (input) input.value = '';
        }
        
        // Immediately update URL with new style (before saveSelectionsToStorage)
        // This ensures the URL is updated right away
        const currentSizeCode = widgetState.selectedSizeCode || '';
        const engravingInput = sectionEl.querySelector('.tp-engraving-input') || document.querySelector('.tp-engraving-input');
        const engravingFontSelect = sectionEl.querySelector('#engraving-font-input') || document.querySelector('#engraving-font-input') || document.querySelector('select[id*="engraving-font"]');
        const engravingText = engravingInput ? engravingInput.value.trim() : '';
        const engravingFont = engravingFontSelect ? engravingFontSelect.value.trim() : '';
        
        console.log('[RamboWidget] 🔵 Style changed - widgetState:', {
          selectedStyleIndex: widgetState.selectedStyleIndex,
          selectedStyleCode: widgetState.selectedStyleCode,
          selectedStyleLabel: widgetState.selectedStyleLabel
        });
        
        // Update URL immediately with new style
        updateUrlParams(
          widgetState.selectedStyleLabel,
          widgetState.selectedStyleIndex,
          widgetState.selectedStyleCode,
          currentSizeCode,
          engravingText,
          engravingFont
        );
        
        // Save to localStorage (this will also update URL again, but that's fine)
        saveSelectionsToStorage();

        // Dispatch custom event for style change
        const event = new CustomEvent('ramboRingStyleChanged', {
          detail: {
            styleIndex: clickedIndex,
            styleLabel: ringConfig.styles[clickedIndex].label,
            styleCode: ringConfig.styles[clickedIndex].code
          },
          bubbles: true
        });
        document.dispatchEvent(event);
        
        // DIRECT UPDATE: Find favorites button and set the style label on it
        const favoritesButton = document.querySelector('.product-gallery__favorites-button');
        if (favoritesButton) {
          favoritesButton.setAttribute('data-selected-style-label', ringConfig.styles[clickedIndex].label);
        }

        // Gallery filtering is handled by tp-gallery-filter.liquid
        // Dispatch both event names for compatibility
        try {
          const styleData = ringConfig.styles[clickedIndex];
          console.log('[RamboWidget] 📤 Dispatching gallery events for style:', styleData?.label);
          
          window.dispatchEvent(new CustomEvent('ringStyleChanged', { 
            detail: { styleLabel: styleData?.label, styleIndex: clickedIndex }
          }));
          document.dispatchEvent(new CustomEvent('ramboRingStyleChanged', { 
            detail: { styleCode: styleData?.code, styleLabel: styleData?.label, styleIndex: clickedIndex }
          }));
          
          console.log('[RamboWidget] ✅ Gallery events dispatched successfully');
        } catch (err) {
          console.error('[RamboWidget] ❌ Error dispatching events:', err);
        }

        // Save to localStorage
        saveSelectionsToStorage();
      }; // End of clickHandler function
      
      // Add our click handler in CAPTURE phase to run FIRST, before any other handlers
      // Using capture phase (true) ensures our handler runs before bubbling phase handlers
      swatch.addEventListener('click', clickHandler, { capture: true, once: false, passive: false });
      
      console.log('[RamboWidget] ✅ Click handler attached to swatch index', index, 'in CAPTURE phase');
    });
    
    // Set initial visual state after all swatches are wired up - AGGRESSIVE
    if (widgetState.selectedStyleIndex >= 0) {
      // Handle default case
      if (widgetState.selectedStyleIndex === -1 && !hasUrlStyleParams) {
        widgetState.selectedStyleIndex = 0;
      }
      // Force visual state IMMEDIATELY and MULTIPLE times
      forceSetVisualState(); // Immediate
      setTimeout(() => { forceSetVisualState(); }, 0); // Next tick
      setTimeout(() => { forceSetVisualState(); }, 10); // 10ms
      setTimeout(() => { forceSetVisualState(); }, 50); // 50ms
      setTimeout(() => { forceSetVisualState(); }, 100); // 100ms
      setTimeout(() => { forceSetVisualState(); }, 200); // 200ms
      console.log('[RamboWidget] ✅✅✅ Initial visual state setters scheduled');
    }

    // Initialize with selected style (whether from URL, prefilter, or default)
    // BUT only if user hasn't manually selected a style
    if (widgetState.selectedStyleIndex >= 0 && widgetState.selectedStyleIndex < ringConfig.styles.length && !widgetState.userManuallySelectedStyle) {
      const selectedStyle = ringConfig.styles[widgetState.selectedStyleIndex];
      updateSizeDropdown(selectedStyle.variants, ringConfig, widgetState.selectedStyleIndex);
      
      // Force initial gallery filter via event (both event names for compatibility)
      window.dispatchEvent(new CustomEvent('ringStyleChanged', { 
        detail: { styleLabel: selectedStyle.label, styleIndex: widgetState.selectedStyleIndex }
      }));
      document.dispatchEvent(new CustomEvent('ramboRingStyleChanged', { 
        detail: { styleCode: selectedStyle.code, styleLabel: selectedStyle.label, styleIndex: widgetState.selectedStyleIndex }
      }));
      
      // Programmatically click the swatch to trigger all visual updates
      // Use retry logic to ensure DOM is ready
      console.log('[RamboWidget] Will click swatch at index:', widgetState.selectedStyleIndex);
      
      function clickSwatchWithRetry(attempts) {
        // Check if user manually selected a style while we were waiting
        if (widgetState.userManuallySelectedStyle) {
          console.log('[RamboWidget] User manually selected style, skipping programmatic click');
          return;
        }
        
        const allSwatches = stylePicker.querySelectorAll('.ring-style-picker__swatch');
        const targetSwatch = allSwatches[widgetState.selectedStyleIndex];
        console.log('[RamboWidget] Attempt', attempts, '- Target swatch:', targetSwatch, 'total swatches:', allSwatches.length);
        
        if (targetSwatch) {
          // FORCE visual state using centralized function
          forceSetVisualState();
          
          // Trigger click for full initialization (this will also update state)
          targetSwatch.click();
          console.log('[RamboWidget] Swatch clicked successfully!');
          
          // Verify and restore visual state after click (in case click cleared it)
          setTimeout(() => {
            forceSetVisualState();
          }, 50);
          
          // Also dispatch the style change event (both names for compatibility)
          const styleData = ringConfig.styles[widgetState.selectedStyleIndex];
          if (styleData) {
            window.dispatchEvent(new CustomEvent('ringStyleChanged', { 
              detail: { styleLabel: styleData.label, styleIndex: widgetState.selectedStyleIndex }
            }));
            document.dispatchEvent(new CustomEvent('ramboRingStyleChanged', { 
              detail: { styleCode: styleData.code, styleLabel: styleData.label, styleIndex: widgetState.selectedStyleIndex }
            }));
          }
        } else if (attempts < 10) {
          // Retry after delay
          setTimeout(() => clickSwatchWithRetry(attempts + 1), 100);
        } else {
          console.log('[RamboWidget] Failed to find swatch after 10 attempts');
        }
      }
      
      // Start with a small delay
      setTimeout(() => clickSwatchWithRetry(1), 100);
    }
    
    // If we restored a style from localStorage (and it's different from what was clicked above), click it
    // This handles the case where restoration happened before swatches were wired up
    if (savedStyleIndexToRestore !== null && savedStyleIndexToRestore >= 0) {
      console.log('[RamboWidget] 🔄 Clicking restored style swatch from localStorage:', savedStyleIndexToRestore);
      
      function clickRestoredSwatchWithRetry(attempts) {
        // Check if user manually selected a style - if so, don't override it
        if (widgetState.userManuallySelectedStyle) {
          console.log('[RamboWidget] ⚠️ User manually selected style, skipping localStorage restoration');
          return;
        }
        
        const allSwatches = stylePicker.querySelectorAll('.ring-style-picker__swatch');
        const targetSwatch = allSwatches[savedStyleIndexToRestore];
        console.log('[RamboWidget] Restore attempt', attempts, '- Target swatch:', targetSwatch, 'total swatches:', allSwatches.length);
        
        if (targetSwatch) {
          // Ensure widgetState is correct FIRST
          widgetState.selectedStyleIndex = savedStyleIndexToRestore;
          widgetState.selectedStyleCode = savedStyleCode || ringConfig.styles[savedStyleIndexToRestore].code || '';
          widgetState.selectedStyleLabel = savedStyleLabel || ringConfig.styles[savedStyleIndexToRestore].label || '';
          
          // FORCE visual state using centralized function
          forceSetVisualState();
          
          // Update size dropdown
          updateSizeDropdown(ringConfig.styles[savedStyleIndexToRestore].variants, ringConfig, savedStyleIndexToRestore);
          
          // Trigger click for full initialization (this will also update state)
          targetSwatch.click();
          console.log('[RamboWidget] ✅ Restored swatch clicked successfully!');
          
          // Verify and restore visual state after click (in case click cleared it)
          setTimeout(() => {
            forceSetVisualState();
          }, 50);
          
          // Dispatch style change events
          const styleData = ringConfig.styles[savedStyleIndexToRestore];
          if (styleData) {
            window.dispatchEvent(new CustomEvent('ringStyleChanged', { 
              detail: { styleLabel: styleData.label, styleIndex: savedStyleIndexToRestore }
            }));
            document.dispatchEvent(new CustomEvent('ramboRingStyleChanged', { 
              detail: { styleCode: styleData.code, styleLabel: styleData.label, styleIndex: savedStyleIndexToRestore }
            }));
          }
        } else if (attempts < 15) {
          // Retry after delay (more attempts for restoration)
          setTimeout(() => clickRestoredSwatchWithRetry(attempts + 1), 150);
        } else {
          console.warn('[RamboWidget] ⚠️ Failed to find restored swatch after 15 attempts');
        }
      }
      
      // Start with a delay to ensure swatches are fully wired up
      // But check if user manually selected before running
      setTimeout(() => {
        if (widgetState.userManuallySelectedStyle) {
          console.log('[RamboWidget] ⚠️ User manually selected style before restoration could run, skipping');
          return;
        }
        clickRestoredSwatchWithRetry(1);
      }, 300);
    }
    
    // Update price if both style and size are selected
    if (widgetState.selectedStyleIndex >= 0 && widgetState.selectedSizeCode) {
      updatePrice(ringConfig, widgetState.selectedStyleIndex, widgetState.selectedSizeCode);
    }
    
    // CONTINUOUS MONITOR: Ensure visual state matches widgetState
    // This handles cases where visual state gets cleared (browser back button, page cache, etc.)
    let monitorInterval = setInterval(() => {
      if (isUpdatingVisualState) return; // Skip if already updating
      if (widgetState.selectedStyleIndex >= 0 && widgetState.selectedStyleIndex < ringConfig.styles.length) {
        const activeSwatch = stylePicker.querySelector('.ring-style-picker__swatch--active');
        const allSwatches = stylePicker.querySelectorAll('.ring-style-picker__swatch');
        const targetSwatch = allSwatches[widgetState.selectedStyleIndex];
        
        // Check if the correct swatch is active
        if (targetSwatch && (!activeSwatch || activeSwatch !== targetSwatch)) {
          isUpdatingVisualState = true;
          forceSetVisualState();
          setTimeout(() => { isUpdatingVisualState = false; }, 100);
        }
      }
    }, 2000); // Check every 2 seconds
    
    // Store interval so we can clear it if needed
    if (!window.ramboWidgetMonitors) {
      window.ramboWidgetMonitors = [];
    }
    window.ramboWidgetMonitors.push(monitorInterval);
    
    console.log('[RamboWidget] ✅ Continuous visual state monitor started');
    
    // AGGRESSIVE FINAL SET: Force visual state multiple times to ensure it sticks
    // This handles cases where restoration happens in multiple phases
    setTimeout(() => {
      forceSetVisualState();
    }, 100);
    setTimeout(() => {
      forceSetVisualState();
    }, 300);
    setTimeout(() => {
      forceSetVisualState();
    }, 600);
    setTimeout(() => {
      forceSetVisualState();
    }, 1000);
    console.log('[RamboWidget] ✅✅✅ Aggressive visual state setters scheduled');

    // Set up engraving field listeners to save on change
    function setupEngravingListeners() {
      // Try multiple selectors - sectionEl, engraving section, then document
      const engravingSection = sectionEl.querySelector('.tp-engraving-section') || 
                               document.querySelector('.tp-engraving-section');
      
      const engravingInput = (engravingSection && engravingSection.querySelector('.tp-engraving-input')) ||
                             sectionEl.querySelector('.tp-engraving-input') || 
                             document.querySelector('.tp-engraving-input');
      
      const engravingFontHiddenInput = (engravingSection && engravingSection.querySelector('#engraving-font-input')) ||
                                       sectionEl.querySelector('#engraving-font-input') || 
                                       document.querySelector('#engraving-font-input');
      
      const engravingFontSelect = (engravingSection && engravingSection.querySelector('select[id*="engraving-font"]')) ||
                                  sectionEl.querySelector('select[id*="engraving-font"]') || 
                                  document.querySelector('select[id*="engraving-font"]');
      
      console.log('[RamboWidget] Setting up engraving listeners - Input:', !!engravingInput, 'Font Hidden:', !!engravingFontHiddenInput, 'Font Select:', !!engravingFontSelect);
      
      // Engraving text input
      if (engravingInput) {
        // Check if listener already attached
        if (!engravingInput.dataset.ramboListenerAttached) {
          // Save on input (for text changes) - use debounce to avoid too many saves
          let inputTimeout;
          engravingInput.addEventListener('input', function() {
            clearTimeout(inputTimeout);
            inputTimeout = setTimeout(function() {
              console.log('[RamboWidget] Engraving text changed:', engravingInput.value.substring(0, 20) + '...');
              saveSelectionsToStorage();
            }, 300);
          });
          // Also save on blur (when user leaves the field)
          engravingInput.addEventListener('blur', function() {
            console.log('[RamboWidget] Engraving text blurred, saving...');
            saveSelectionsToStorage();
          });
          engravingInput.dataset.ramboListenerAttached = 'true';
          console.log('[RamboWidget] Engraving text listener attached');
        }
      } else {
        console.warn('[RamboWidget] Engraving text input not found - tried sectionEl, engravingSection, and document');
      }
      
      // Font selector - handle both custom dropdown (hidden input) and regular select
      if (engravingFontHiddenInput) {
        // Check if listener already attached
        if (!engravingFontHiddenInput.dataset.ramboListenerAttached) {
          // Override the value property to catch ALL changes (including programmatic)
          const originalValueDescriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
          let lastFontValue = engravingFontHiddenInput.value;
          
          Object.defineProperty(engravingFontHiddenInput, 'value', {
            get: function() {
              return originalValueDescriptor ? originalValueDescriptor.get.call(this) : this.getAttribute('value') || '';
            },
            set: function(newValue) {
              if (originalValueDescriptor && originalValueDescriptor.set) {
                originalValueDescriptor.set.call(this, newValue);
              } else {
                this.setAttribute('value', newValue);
              }
              // Save when value changes
              if (newValue !== lastFontValue) {
                console.log('[RamboWidget] Font value setter triggered:', newValue);
                lastFontValue = newValue;
                setTimeout(function() {
                  saveSelectionsToStorage();
                }, 50);
              }
            },
            configurable: true,
            enumerable: true
          });
          
          // Listen for clicks on font option buttons (most reliable - catches user selection)
          const fontOptions = (sectionEl.querySelectorAll('.tp-select-option') || []).length > 0 
            ? sectionEl.querySelectorAll('.tp-select-option')
            : document.querySelectorAll('.tp-select-option');
          
          console.log('[RamboWidget] Found', fontOptions.length, 'font options');
          
          fontOptions.forEach(function(option) {
            // Check if listener already attached
            if (!option.dataset.ramboListenerAttached) {
              // Use capture phase to catch before other handlers
              option.addEventListener('click', function() {
                console.log('[RamboWidget] Font option clicked:', this.dataset.value);
                // Delay to let the hidden input update first
                setTimeout(function() {
                  saveSelectionsToStorage();
                }, 200);
              }, true);
              option.dataset.ramboListenerAttached = 'true';
            }
          });
          
          // Watch for value changes on hidden input (faster periodic check as fallback)
          if (!engravingFontHiddenInput._fontWatcher) {
            const fontValueWatcher = setInterval(function() {
              const currentValue = engravingFontHiddenInput.value;
              if (currentValue !== lastFontValue) {
                console.log('[RamboWidget] Font value changed from', lastFontValue, 'to', currentValue, ', saving...');
                lastFontValue = currentValue;
                saveSelectionsToStorage();
              }
            }, 200); // Faster check
            
            // Store watcher ID on the element so we can clear it later if needed
            engravingFontHiddenInput._fontWatcher = fontValueWatcher;
          }
          
          // Also listen for input/change events (in case they're dispatched)
          engravingFontHiddenInput.addEventListener('input', function() {
            console.log('[RamboWidget] Font input event, saving...');
            saveSelectionsToStorage();
          });
          engravingFontHiddenInput.addEventListener('change', function() {
            console.log('[RamboWidget] Font change event, saving...');
            saveSelectionsToStorage();
          });
          
          engravingFontHiddenInput.dataset.ramboListenerAttached = 'true';
          console.log('[RamboWidget] Engraving font listener attached (value setter override + options + watcher)');
        }
      } else if (engravingFontSelect) {
        // Regular select dropdown
        if (!engravingFontSelect.dataset.ramboListenerAttached) {
          engravingFontSelect.addEventListener('change', function() {
            console.log('[RamboWidget] Font select changed, saving...');
            saveSelectionsToStorage();
          });
          engravingFontSelect.dataset.ramboListenerAttached = 'true';
          console.log('[RamboWidget] Engraving font listener attached (select)');
        }
      } else {
        console.warn('[RamboWidget] Engraving font input not found - sectionEl:', !!sectionEl, 'document:', !!document.querySelector('#engraving-font-input'));
      }
    }
    
    // Try to set up listeners multiple times to catch late-loading elements
    setupEngravingListeners();
    
    // Retry with increasing delays to catch elements that load after other scripts
    setTimeout(setupEngravingListeners, 300);
    setTimeout(setupEngravingListeners, 600);
    setTimeout(setupEngravingListeners, 1000);
    setTimeout(setupEngravingListeners, 2000);
    
    // Also listen for when font options are dynamically added
    const fontOptionsContainer = sectionEl.querySelector('#font-options') || document.querySelector('#font-options');
    if (fontOptionsContainer) {
      const fontOptionsObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.addedNodes.length > 0) {
            console.log('[RamboWidget] Font options container changed, re-setting up listeners...');
            setTimeout(setupEngravingListeners, 100);
          }
        });
      });
      fontOptionsObserver.observe(fontOptionsContainer, { childList: true, subtree: true });
    }
  }

  /**
   * Update size dropdown options based on selected style (using button grid)
   */
  function updateSizeDropdown(variants, ringConfig, styleIndex) {
    const sectionEl = document.getElementById(SECTION_ID);
    if (!sectionEl) return;
    
    const optionsContainer = sectionEl.querySelector('#rambo-ring-size-options');
    if (!optionsContainer) return;
    
    // Clear existing options
    optionsContainer.innerHTML = '';
    console.log('[RamboWidget] 📏 Creating size options for', variants.length, 'variants');
    
    // Add options for each variant (using button structure)
    variants.forEach((variant, index) => {
      const optionEl = document.createElement('button');
      optionEl.type = 'button';
      optionEl.className = 'tp-size-option';
      optionEl.dataset.size = variant.sizeCode;
      
      if (widgetState.selectedSizeCode === variant.sizeCode) {
        optionEl.classList.add('active');
        console.log('[RamboWidget] 📏 Size option', variant.sizeCode, 'marked as active (restored)');
      }
      
      let cleanSizeLabel = cleanRingSizeLabel(variant.sizeLabel || variant.sizeCode);
      optionEl.textContent = cleanSizeLabel;
      console.log('[RamboWidget] 📏 Created size button:', variant.sizeCode, 'Label:', cleanSizeLabel);
      
      // Add click handler to select this size
      // Use capture phase and stop propagation to ensure this handler runs
      optionEl.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        const clickedSizeCode = variant.sizeCode;
        console.log('[RamboWidget] 🔵🔵🔵 SIZE OPTION CLICKED:', clickedSizeCode, 'Button:', this);
        
        // Update active state
        sectionEl.querySelectorAll('.tp-size-option').forEach(btn => btn.classList.remove('active'));
        optionEl.classList.add('active');
        console.log('[RamboWidget] ✅ Size button marked as active');
        
        // Update hidden input FIRST
        const sizeInput = sectionEl.querySelector('#rambo-ring-size-input');
        if (sizeInput) {
          sizeInput.value = clickedSizeCode;
          console.log('[RamboWidget] ✅ Size input updated to:', sizeInput.value, 'Input element:', sizeInput);
        } else {
          console.error('[RamboWidget] ❌❌❌ Size input not found!');
          return; // Can't proceed without input
        }
        
        // Update price
        updatePrice(ringConfig, styleIndex, clickedSizeCode);
        
        // Update shared state IMMEDIATELY (this is the source of truth)
        widgetState.selectedSizeCode = clickedSizeCode;
        console.log('[RamboWidget] ✅ widgetState.selectedSizeCode set to:', widgetState.selectedSizeCode);
        console.log('[RamboWidget] ✅ Current widgetState:', JSON.stringify({
          selectedSizeCode: widgetState.selectedSizeCode,
          selectedStyleIndex: widgetState.selectedStyleIndex
        }));
        
        // Save to localStorage IMMEDIATELY (don't wait - widgetState is already set)
        // The size is now in widgetState, so saveSelectionsToStorage will find it
        console.log('[RamboWidget] 🔵 About to call saveSelectionsToStorage with size:', widgetState.selectedSizeCode);
        saveSelectionsToStorage();
        
        // Update Add to Cart button state immediately after size is selected
        if (typeof window.updateStickyBar === 'function') {
          setTimeout(() => {
            window.updateStickyBar();
            console.log('[RamboWidget] ✅ Updated Add to Cart button state after size selection');
          }, 50);
        }
        
        // Also save again after a tiny delay to ensure everything is synced
        setTimeout(() => {
          console.log('[RamboWidget] 🔵 Second save call - verifying size:', widgetState.selectedSizeCode);
          if (widgetState.selectedSizeCode) {
            saveSelectionsToStorage();
          } else {
            console.error('[RamboWidget] ❌ Size was lost! widgetState.selectedSizeCode is empty!');
          }
        }, 100);
      }, true); // Use capture phase
      
      optionsContainer.appendChild(optionEl);
    });
  }

  /**
   * Update displayed price based on selected variant
   */
  function updatePrice(ringConfig, styleIndex, sizeCode) {
    if (!sizeCode) return;

    const style = ringConfig.styles[styleIndex];
    const variant = style.variants.find(v => v.sizeCode === sizeCode);
    if (!variant) return;

    // Find price display elements and update them
    const sectionEl = document.getElementById(SECTION_ID);
    if (!sectionEl) return;

    const price = parseFloat(variant.price);
    const compareAtPrice = variant.compareAtPrice ? parseFloat(variant.compareAtPrice) : null;

    // Update current price element
    const currentPriceEl = sectionEl.querySelector('.tp-price-current');
    if (currentPriceEl) {
      currentPriceEl.textContent = `$${price.toFixed(2)}`;
    }

    // Update compare price element
    const comparePriceEl = sectionEl.querySelector('.tp-price-compare');
    if (comparePriceEl) {
      if (compareAtPrice && compareAtPrice > price) {
        comparePriceEl.textContent = `$${compareAtPrice.toFixed(2)}`;
        comparePriceEl.style.display = '';
      } else {
        comparePriceEl.style.display = 'none';
      }
    }

    // Update Add to Cart button price
    const addToCartText = sectionEl.querySelector('.tp-add-to-cart-text');
    if (addToCartText) {
      addToCartText.textContent = `Add to Cart - $${price.toFixed(2)}`;
    }
  }

  /**
   * Override Add to Cart button to use default variant + line item properties
   */
  function overrideAddToCart(ringConfig) {
    const sectionEl = document.getElementById(SECTION_ID);
    if (!sectionEl) return;
    
    // Find the actual product area, not the whole section (which might include header)
    // Look for product-info or product-form areas specifically
    let productArea = sectionEl.querySelector('.product-info, .product, product-form, [class*="product"]');
    
    // If not found, try to find the main product container
    if (!productArea) {
      productArea = sectionEl.querySelector('product-rerender, [id*="product-info"]') || sectionEl;
    }
    
    // Final fallback: use section but exclude header
    if (!productArea || productArea === sectionEl) {
      // Create a more specific search area by excluding header
      const header = sectionEl.querySelector('header, .header');
      if (header) {
        // Search in section but not in header
        productArea = sectionEl;
      } else {
        productArea = sectionEl;
      }
    }

    // Get default variant ID from Liquid (should be exposed in a data attribute or script tag)
    const defaultVariantId = getDefaultVariantId();
    if (!defaultVariantId) {
      return;
    }

    // Find product form - try multiple selectors
    // The theme uses <product-form> custom element with a form inside
    // IMPORTANT: Don't match localization forms or other non-product forms
    let productForm = null;
    
    // Helper function to safely check if form ID/action contains a string
    const formMatches = (form, pattern) => {
      const formId = form.id || '';
      const formAction = form.action || '';
      return (typeof formId === 'string' && formId.includes(pattern)) ||
             (typeof formAction === 'string' && formAction.includes(pattern));
    };
    
    // Try finding the product-form custom element first (most reliable)
    // Search in product area, not whole section (to avoid header forms)
    const productFormElement = productArea.querySelector('product-form');
    if (productFormElement) {
      const form = productFormElement.querySelector('form');
      // Verify it's actually a product form (has name="id" input, not localization)
      if (form && form.querySelector('input[name="id"]') && 
          !formMatches(form, 'localization') &&
          (formMatches(form, 'product-form') || formMatches(form, '/cart/add') || !form.action)) {
        productForm = form;
      }
    }
    
    // Fallback: look for form with product form ID pattern
    if (!productForm) {
      const forms = productArea.querySelectorAll('form[id*="product-form"]');
      for (const form of forms) {
        if (form.querySelector('input[name="id"]') && !formMatches(form, 'localization')) {
          productForm = form;
          break;
        }
      }
    }
    
    // Fallback: look for form with cart/add action
    if (!productForm) {
      const forms = productArea.querySelectorAll('form[action*="/cart/add"]');
      for (const form of forms) {
        if (form.querySelector('input[name="id"]') && !formMatches(form, 'localization')) {
          productForm = form;
          break;
        }
      }
    }
    
    // Last resort: find form with name="id" input (product forms have this)
    // But ONLY in the product area, not in header/navigation
    if (!productForm) {
      const allForms = productArea.querySelectorAll('form');
      for (const form of allForms) {
        // Skip localization forms explicitly (check ID, action, and class)
        if (formMatches(form, 'localization') || 
            (form.action && form.action === '/localization') ||
            form.classList.contains('shopify-localization-form')) {
          continue;
        }
        // Skip forms in header/nav areas
        if (form.closest('header, nav, .header, .nav')) {
          continue;
        }
        // Product forms have input[name="id"] for variant selection with a value
        const variantInput = form.querySelector('input[name="id"]');
        if (variantInput && variantInput.value && !variantInput.disabled) {
          // Double-check: product forms don't have localization in their ID
          if (!formMatches(form, 'localization')) {
            productForm = form;
            break;
          }
        }
      }
    }
    
    if (!productForm) {
      const allForms = Array.from(productArea.querySelectorAll('form'));
      
      // Last attempt: try to find ANY form that's not localization and has a variant input
      for (const form of allForms) {
        if (formMatches(form, 'localization') ||
            form.classList.contains('shopify-localization-form')) {
          continue;
        }
        
        const variantInput = form.querySelector('input[name="id"]');
        if (variantInput) {
          productForm = form;
          break;
        }
      }
      
      if (!productForm) {
        return;
      }
    }
    
    // Final verification - make absolutely sure it's not a localization form
    if (formMatches(productForm, 'localization') ||
        productForm.classList.contains('shopify-localization-form')) {
      return;
    }

    // Find Add to Cart button - try multiple selectors
    const addToCartButton = productForm.querySelector('button[type="submit"], button[name="add"], .button--primary, [data-product-add-to-cart]');
    if (!addToCartButton) {
      return;
    }

    // Track if we're already processing to prevent duplicate submissions
    // Store on the section element so it's accessible
    if (!sectionEl.dataset.isProcessing) {
      sectionEl.dataset.isProcessing = 'false';
    }

    // Override form submission
    productForm.addEventListener('submit', function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      if (sectionEl.dataset.isProcessing === 'true') {
        console.log('rambo-ring-widget: Already processing, ignoring duplicate submit');
        return false;
      }
      
      sectionEl.dataset.isProcessing = 'true';
      handleCustomAddToCart(ringConfig, defaultVariantId, sectionEl);
      
      return false;
    });

    // Also handle direct button clicks (prevent default to avoid double submission)
    addToCartButton.addEventListener('click', function(e) {
      // Only prevent default if we're inside the form to avoid double submission
      if (this.closest('form') === productForm) {
        e.preventDefault();
        e.stopPropagation();
        
        if (sectionEl.dataset.isProcessing === 'true') {
          console.log('rambo-ring-widget: Already processing, ignoring duplicate click');
          return false;
        }
        
        sectionEl.dataset.isProcessing = 'true';
        handleCustomAddToCart(ringConfig, defaultVariantId, sectionEl);
      }
    });
  }

  /**
   * Filter gallery images based on selected ring style
   * Shows only images that match the selected style code (e.g., "01", "02")
   * Also shows global images (those without style numbers in filename)
   */
  function filterGalleryByStyle(styleCode, sectionEl) {
    if (!sectionEl || !styleCode) {
      return;
    }

    // Extract numeric part from style code (e.g., "STYLE_01" -> "01", "01" -> "01", "1" -> "01")
    const normalizeStyleNumber = (codeStr) => {
      // Extract all digits from the code
      const clean = String(codeStr || '').replace(/\D/g, '');
      if (!clean) return null;
      // Normalize to 2 digits (e.g., "1" -> "01", "01" -> "01", "12" -> "12")
      if (clean.length === 1) return '0' + clean;
      return clean;
    };

    const normalizedStyleCode = normalizeStyleNumber(styleCode);
    if (!normalizedStyleCode) {
      return;
    }

    // Get style number from filename (e.g., "style05" -> "05")
    const getStyleNumberFromFilename = (filename) => {
      const lower = filename.toLowerCase();
      const match = lower.match(/style[-_]?(\d{1,2})/);
      if (match && match[1]) {
        const num = match[1];
        return num.length === 1 ? '0' + num : num;
      }
      return null;
    };

    // Get filename from image src
    const getFileNameFromImg = (img) => {
      if (!img) return '';
      const src = img.currentSrc || img.src || '';
      if (!src) return '';
      const file = src.split('/').pop().split('?')[0];
      return file.trim().toLowerCase();
    };

    // Collect all thumbnails and main media
    const collectThumbnails = () => {
      const selectors = '.product-gallery__thumbnail, .product-gallery__thumbnail-item, .product-gallery__thumb';
      let nodes = sectionEl.querySelectorAll(selectors);
      
      // Fallback: if no thumbnails, use main media
      if (!nodes.length) {
        nodes = sectionEl.querySelectorAll('.product-gallery__media');
      }

      const meta = [];
      Array.from(nodes).forEach((node) => {
        // Skip if node is detached from DOM
        if (!node || !node.parentNode) return;
        
        const img = (node.tagName.toLowerCase() === 'img') ? node : node.querySelector('img');
        if (!img) return;

        const filename = getFileNameFromImg(img);
        const styleNum = getStyleNumberFromFilename(filename);
        
        const controlNode = node;
        // Safely get data-media-id - check if methods exist
        let mediaId = null;
        try {
          if (controlNode && typeof controlNode.getAttribute === 'function') {
            mediaId = controlNode.getAttribute('data-media-id');
          }
          if (!mediaId && node && typeof node.getAttribute === 'function') {
            mediaId = node.getAttribute('data-media-id');
          }
          if (!mediaId && img) {
            const mediaContainer = img.closest('[data-media-id]');
            if (mediaContainer && typeof mediaContainer.getAttribute === 'function') {
              mediaId = mediaContainer.getAttribute('data-media-id');
            }
          }
        } catch (e) {
          // Silent fail
        }

        meta.push({
          node: controlNode,
          filename: filename,
          styleNum: styleNum, // null = global, "05" = specific
          mediaId: mediaId
        });
      });
      return meta;
    };

    // Apply filter
    const thumbnails = collectThumbnails();
    
    if (thumbnails.length === 0) {
      return;
    }
    
    let firstMatchMediaId = null;

    thumbnails.forEach((item) => {
      let shouldShow = false; // Default to HIDE - only show if explicitly matched
      const filenameLower = item.filename.toLowerCase();

      // ALWAYS SHOW: images with "ring-styles" in filename (global)
      if (filenameLower.indexOf('ring-styles') !== -1) {
        shouldShow = true;
      }
      // ALWAYS SHOW: spot images (global)
      else if (filenameLower.indexOf('spot') !== -1) {
        shouldShow = true;
      }
      else if (item.styleNum) {
        // Style-specific image: show only if it matches selected style
        shouldShow = (item.styleNum === normalizedStyleCode);
      } else {
        // Global image (no style number): HIDE by default
        shouldShow = false;
      }
      
      // Capture first style-specific match for scrolling
      if (shouldShow && item.styleNum && !firstMatchMediaId && item.mediaId) {
        firstMatchMediaId = item.mediaId;
      }

      // Toggle visibility - safely check if nodes exist before manipulating
      if (shouldShow) {
        if (item.node && item.node.parentNode) {
          try {
            item.node.style.display = '';
            item.node.removeAttribute('hidden');
          } catch (e) {
            // Silent fail
          }
        }
        if (item.mediaId) {
          try {
            const mainSelector = `.product-gallery__media[data-media-id="${item.mediaId}"]`;
            const mediaNode = sectionEl.querySelector(mainSelector);
            if (mediaNode && mediaNode.parentNode) {
              mediaNode.style.display = '';
              mediaNode.removeAttribute('hidden');
            }
          } catch (e) {
            // Silent fail
          }
        }
      } else {
        if (item.node && item.node.parentNode) {
          try {
            item.node.style.setProperty('display', 'none', 'important');
            item.node.setAttribute('hidden', '');
          } catch (e) {
            // Silent fail
          }
        }
        if (item.mediaId) {
          try {
            const mainSelector = `.product-gallery__media[data-media-id="${item.mediaId}"]`;
            const mediaNode = sectionEl.querySelector(mainSelector);
            if (mediaNode && mediaNode.parentNode) {
              mediaNode.style.setProperty('display', 'none', 'important');
              mediaNode.setAttribute('hidden', '');
            }
          } catch (e) {
            // Silent fail
          }
        }
      }
    });

    // After filtering, scroll gallery to the first visible image (the hero image)
    setTimeout(() => {
      try {
        // Find the target media element (either first match ID or first visible)
        let targetMedia = null;
        if (firstMatchMediaId) {
          targetMedia = sectionEl.querySelector(`.product-gallery__media[data-media-id="${firstMatchMediaId}"]`);
        }
        
        if (!targetMedia) {
          targetMedia = sectionEl.querySelector('.product-gallery__media:not([hidden]):not([style*="display: none"])');
        }
        
        if (targetMedia) {
          // Check if we are in a desktop slider/grid or mobile carousel
          const galleryContainer = targetMedia.closest('.product-gallery__media-list') || 
                                  targetMedia.closest('.product-gallery__carousel') ||
                                  targetMedia.closest('.product-gallery') ||
                                  targetMedia.closest('.scroller') || // Common scroller class
                                  targetMedia.parentNode; // Fallback to immediate parent
          
          if (galleryContainer) {
            // Calculate position relative to container
            const top = targetMedia.offsetTop;
            const left = targetMedia.offsetLeft;
            
            // Force smooth scroll
            galleryContainer.scrollTo({ top: top, left: left, behavior: 'smooth' });
          }
          
          // Also try standard scrollIntoView as backup
          targetMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          
          // Try clicking associated thumbnail
          if (firstMatchMediaId) {
             const thumb = sectionEl.querySelector(`.product-gallery__thumbnail[data-media-id="${firstMatchMediaId}"] button, .product-gallery__thumbnail[data-target-media-id="${firstMatchMediaId}"]`);
             if (thumb) thumb.click();
          }
        }
      } catch (e) {
        // Silent fail
      }
    }, 100); // Increased delay slightly

    // Sync dots navigation (mobile)
    try {
      const mediaNodes = sectionEl.querySelectorAll('.product-gallery__media[data-media-id]');
      const dotsNav = sectionEl.querySelector('carousel-navigation.page-dots');
      if (mediaNodes.length && dotsNav) {
        const dots = dotsNav.querySelectorAll('button.tap-area');
        Array.from(mediaNodes).forEach((mediaNode, idx) => {
          if (dots[idx]) {
            const isVisible = (mediaNode.style.display !== 'none' && !mediaNode.hasAttribute('hidden'));
            if (isVisible) {
              dots[idx].style.display = '';
              dots[idx].removeAttribute('hidden');
            } else {
              dots[idx].style.setProperty('display', 'none', 'important');
              dots[idx].setAttribute('hidden', '');
            }
          }
        });
      }
    } catch (e) {
      // Ignore errors
    }
  }

  /**
   * Get default variant ID from page
   */
  function getDefaultVariantId() {
    // Try to get from data attribute (added in main-product.liquid)
    const variantIdAttr = document.querySelector('[data-default-variant-id]')?.dataset?.defaultVariantId;
    if (variantIdAttr) return variantIdAttr;

    // Try to get from script tag with JSON (rambo-product-data)
    const ramboDataScript = document.getElementById('rambo-product-data');
    if (ramboDataScript) {
      try {
        const data = JSON.parse(ramboDataScript.textContent);
        if (data.defaultVariantId) return String(data.defaultVariantId);
      } catch (e) {
        // Silent fail
      }
    }

    // Try to get from other script tags with JSON
    const scriptTags = document.querySelectorAll('script[type="application/json"]');
    for (const script of scriptTags) {
      try {
        const data = JSON.parse(script.textContent);
        if (data.selectedVariant?.id) return String(data.selectedVariant.id);
        if (data.product?.selected_or_first_available_variant?.id) {
          return String(data.product.selected_or_first_available_variant.id);
        }
      } catch (e) {
        // Not JSON, continue
      }
    }

    // Try to get from product form
    const variantInput = document.querySelector('input[name="id"], input[name="variant_id"]');
    if (variantInput) return variantInput.value;

    return null;
  }

  /**
   * Handle custom Add to Cart with line item properties
   */
  function handleCustomAddToCart(ringConfig, defaultVariantId, sectionEl) {
    if (!sectionEl) {
      sectionEl = document.getElementById(SECTION_ID);
      if (!sectionEl) return;
    }

    const stylePicker = sectionEl.querySelector('#rambo-ring-style-picker');
    const selectedSizeInput = sectionEl.querySelector('#rambo-ring-size-input');
    const engravingInput = sectionEl.querySelector('.tp-engraving-input');

    // Reset errors
    const styleError = document.getElementById('rambo-ring-style-error');
    const sizeError = document.getElementById('rambo-ring-size-error');
    const engravingError = document.getElementById('rambo-engraving-error');
    if (styleError) styleError.style.display = 'none';
    if (sizeError) sizeError.style.display = 'none';
    if (engravingError) engravingError.style.display = 'none';

    let hasError = false;
    let firstErrorElement = null;

    // Validate ring style
    if (!stylePicker) {
      if (styleError) {
        styleError.style.display = 'block';
        if (!firstErrorElement) firstErrorElement = styleError;
      }
      hasError = true;
    } else {
      const activeSwatch = stylePicker.querySelector('.ring-style-picker__swatch--active');
      if (!activeSwatch) {
        if (styleError) {
          styleError.style.display = 'block';
          if (!firstErrorElement) firstErrorElement = styleError;
        }
        hasError = true;
      }
    }
    
    // Validate ring size
    if (!selectedSizeInput || !selectedSizeInput.value) {
      if (sizeError) {
        sizeError.style.display = 'block';
        if (!firstErrorElement) firstErrorElement = sizeError;
      }
      hasError = true;
    }
    
    // Validate engraving (if required - check if input has required attribute)
    if (engravingInput && engravingInput.hasAttribute('required')) {
      const engravingValue = engravingInput.value.trim();
      if (!engravingValue) {
        if (engravingError) {
          engravingError.style.display = 'block';
          if (!firstErrorElement) firstErrorElement = engravingError;
        }
        hasError = true;
      }
    }

    if (hasError) {
      // Scroll to first error
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      // Reset processing flag so user can try again
      if (sectionEl) {
        sectionEl.dataset.isProcessing = 'false';
      }
      return;
    }

    const selectedSizeCode = selectedSizeInput.value;

    // Get the active swatch first - it has all the correct data attributes
    const activeSwatch = stylePicker.querySelector('.ring-style-picker__swatch--active');
    if (!activeSwatch) {
      console.error('rambo-ring-widget: No active swatch found!');
      alert('Please select a ring style.');
      return;
    }

    // Use widgetState.selectedStyleIndex as primary source of truth (updated on every swatch click)
    let styleIndex = widgetState.selectedStyleIndex;
    
    // Fallback: get from active swatch if widgetState is not set or invalid
    if (styleIndex < 0 || styleIndex >= ringConfig.styles.length) {
      const swatchIndex = parseInt(activeSwatch.dataset.styleIndex);
      if (!isNaN(swatchIndex) && swatchIndex >= 0 && swatchIndex < ringConfig.styles.length) {
        styleIndex = swatchIndex;
        widgetState.selectedStyleIndex = styleIndex;
      }
    }
    
    // Final validation
    if (isNaN(styleIndex) || styleIndex < 0 || styleIndex >= ringConfig.styles.length) {
      console.error('rambo-ring-widget: Invalid styleIndex =', styleIndex);
      alert('Invalid ring style selection. Please try again.');
      return;
    }
    
    const style = ringConfig.styles[styleIndex];
    const styleLabel = style.label || '';
    
    // Get style code from active swatch's data attribute (most reliable)
    const swatchStyleCode = activeSwatch.dataset.styleCode || '';
    const swatchFilename = activeSwatch.dataset.swatchFilename || '';
    
    console.log('rambo-ring-widget: Adding to cart - Style Index:', styleIndex, 'Style Label:', styleLabel, 'Style Code:', swatchStyleCode, 'Swatch Filename:', swatchFilename, 'Size Code:', selectedSizeCode);
    const variant = style.variants.find(v => v.sizeCode === selectedSizeCode);

    if (!variant) {
      alert('Selected variant not found.');
      return;
    }
    
    // Use the variant's Shopify variant ID if available, otherwise fall back to default
    // Shopify variant IDs must be numeric strings or numbers
    let variantIdToUse = defaultVariantId;
    if (variant.shopifyVariantId) {
      // Ensure it's a valid numeric string or number
      const shopifyVariantIdStr = String(variant.shopifyVariantId).trim();
      const shopifyVariantIdNum = Number(shopifyVariantIdStr);
      if (shopifyVariantIdStr && !isNaN(shopifyVariantIdNum) && shopifyVariantIdNum > 0) {
        variantIdToUse = String(shopifyVariantIdNum);
      }
    }

    // Clean sizeLabel: remove "US" and parentheses, show only the number
    let sizeLabel = cleanRingSizeLabel(variant.sizeLabel || variant.sizeCode);
    
    // Clean up style label: remove "Tungsten" from the text
    let cleanStyleLabel = styleLabel;
    if (cleanStyleLabel) {
      // Remove "Tungsten" (case-insensitive, with surrounding spaces)
      cleanStyleLabel = cleanStyleLabel.replace(/\s+Tungsten\s*/gi, ' ').trim();
      // Clean up any double spaces
      cleanStyleLabel = cleanStyleLabel.replace(/\s+/g, ' ');
    }

    // Get engraving info from the form
    // The engraving fields use section.id (the full section ID) in their IDs
    // SECTION_ID is like "shopify-section-template--23487883706685_main"
    // But section.id passed to the snippet is just "template--23487883706685_main"
    // So we need to extract the part after "shopify-section-"
    const sectionIdForEngraving = SECTION_ID.replace('shopify-section-', '');
    
    // Try multiple selectors to find engraving fields (in order of specificity)
    const engravingTextInput = sectionEl.querySelector('.tp-engraving-input') ||
                               sectionEl.querySelector(`#engraving-text-${sectionIdForEngraving}`) ||
                               sectionEl.querySelector('input[id*="engraving-text"]') ||
                               sectionEl.querySelector('.ring-engraving input[type="text"]') ||
                               sectionEl.querySelector('.ring-engraving__input');
    
    const engravingFontSelect = sectionEl.querySelector('#engraving-font-input') ||
                                sectionEl.querySelector(`#engraving-font-select-${sectionIdForEngraving}`) ||
                                sectionEl.querySelector('select[id*="engraving-font-select"]') ||
                                sectionEl.querySelector('.ring-engraving select') ||
                                sectionEl.querySelector('.ring-engraving__select');
    
    const engravingText = engravingTextInput ? engravingTextInput.value.trim() : '';
    const engravingFont = engravingFontSelect ? engravingFontSelect.value.trim() : '';
    
    // Validate engraving (required fields)
    if (!engravingText) {
      alert('Please enter your inside engraving text.');
      if (engravingTextInput) engravingTextInput.focus();
      return;
    }
    
    if (!engravingFont) {
      alert('Please select an engraving font.');
      if (engravingFontSelect) engravingFontSelect.focus();
      return;
    }

    // Disable button during request
    const addToCartButton = sectionEl.querySelector('button[type="submit"][name="add"], button[data-product-add-to-cart], .product-form__submit');
    if (addToCartButton) {
      addToCartButton.disabled = true;
      addToCartButton.textContent = 'Adding...';
    }

    // Build properties object with ring info AND engraving
    const properties = {
      'Ring Style': cleanStyleLabel, // Use cleaned style label (without "Tungsten")
      'Ring Size': sizeLabel,
      'Ring SKU': variant.sku, // Changed from "Rambo SKU" to "Ring SKU"
      'Inside Engraving Text': engravingText,
      'Engraving Font': engravingFont,
      '_styleIndex': String(styleIndex) // Hidden property for exact style restoration
    };

    // Find the correct HERO image for the selected style from product JSON data
    // Get the style code from the active swatch (e.g., "01", "02") or extract from label
    let normalizedStyleNum = null;
    
    // PRIORITY 1: Use styleCode from swatch data attribute (set by Liquid)
    if (swatchStyleCode) {
      // Extract just the number part (e.g., "STYLE01" -> "01", "01" -> "01")
      const codeMatch = swatchStyleCode.match(/(\d{1,2})/);
      if (codeMatch) {
        normalizedStyleNum = codeMatch[1].length === 1 ? '0' + codeMatch[1] : codeMatch[1];
      }
    }
    
    // PRIORITY 2: Extract from style label (e.g., "01 - Black Tungsten" -> "01")
    if (!normalizedStyleNum && styleLabel) {
      const labelMatch = styleLabel.match(/^(\d{1,2})\s*[-–:]/);
      if (labelMatch) {
        normalizedStyleNum = labelMatch[1].length === 1 ? '0' + labelMatch[1] : labelMatch[1];
      }
    }
    
    // PRIORITY 3: Fallback to styleIndex + 1
    if (!normalizedStyleNum) {
      const styleNum = styleIndex + 1;
      normalizedStyleNum = styleNum < 10 ? '0' + styleNum : String(styleNum);
    }
    
    let productImageUrl = null;
    
    console.log('rambo-ring-widget: Searching for product image for style', normalizedStyleNum, '(styleIndex:', styleIndex, ', styleCode:', swatchStyleCode, ', label:', styleLabel, ')');
    
    // PRIORITY 1: Get HERO image from rambo-product-data JSON (Shopify CDN URLs - most reliable)
    try {
      const productDataEl = document.getElementById('rambo-product-data');
      if (productDataEl) {
        const productData = JSON.parse(productDataEl.textContent);
        const media = productData.media || [];
        
        // First, look for HERO images (main display images)
        for (const m of media) {
          if (m.src) {
            const filename = m.src.split('/').pop().split('?')[0].toLowerCase();
            // Look for HERO images (e.g., style01-hero.webp, style01_hero.webp)
            if (filename.includes(`style${normalizedStyleNum}-hero`) ||
                filename.includes(`style-${normalizedStyleNum}-hero`) ||
                filename.includes(`style_${normalizedStyleNum}_hero`) ||
                filename.includes(`style${normalizedStyleNum}hero`)) {
              productImageUrl = m.src;
              console.log('rambo-ring-widget: ✓✓✓ Found HERO image from product JSON:', productImageUrl);
              break;
            }
          }
        }
        
        // If no hero, look for SWATCH images
        if (!productImageUrl) {
          for (const m of media) {
            if (m.src) {
              const filename = m.src.split('/').pop().split('?')[0].toLowerCase();
              if (filename.includes(`style${normalizedStyleNum}-swatch`) ||
                  filename.includes(`style-${normalizedStyleNum}-swatch`) ||
                  filename.includes(`style_${normalizedStyleNum}_swatch`) ||
                  filename.includes(`style${normalizedStyleNum}swatch`)) {
                productImageUrl = m.src;
                console.log('rambo-ring-widget: ✓✓✓ Found SWATCH image from product JSON:', productImageUrl);
                break;
              }
            }
          }
        }
        
        // If no hero or swatch, look for any image with the style number
        if (!productImageUrl) {
          for (const m of media) {
            if (m.src) {
              const filename = m.src.split('/').pop().split('?')[0].toLowerCase();
              if (filename.includes(`style${normalizedStyleNum}`) ||
                  filename.includes(`style-${normalizedStyleNum}`) ||
                  filename.includes(`style_${normalizedStyleNum}`)) {
                productImageUrl = m.src;
                console.log('rambo-ring-widget: ✓✓✓ Found style image from product JSON:', productImageUrl);
                break;
              }
            }
          }
        }
      }
    } catch (e) {
      console.warn('rambo-ring-widget: Error parsing product data JSON:', e);
    }
    
    // PRIORITY 2: Try active thumbnail in DOM
    if (!productImageUrl) {
      const activeThumbnail = document.querySelector('.tp-thumbnail-btn.active') || sectionEl.querySelector('.tp-thumbnail-btn.active');
      if (activeThumbnail) {
        if (activeThumbnail.dataset.fullImage) {
          productImageUrl = activeThumbnail.dataset.fullImage;
          console.log('rambo-ring-widget: ✓✓✓ Using active thumbnail image:', productImageUrl);
        } else {
          const thumbImg = activeThumbnail.querySelector('img');
          if (thumbImg && thumbImg.src) {
            productImageUrl = thumbImg.src.replace(/width=\d+/, 'width=1200');
            console.log('rambo-ring-widget: ✓✓✓ Using active thumbnail img src:', productImageUrl);
          }
        }
      }
    }
    
    // PRIORITY 3: Use main displayed image
    if (!productImageUrl) {
      const mainImage = sectionEl.querySelector('.tp-main-image') || document.querySelector('.tp-main-image');
      if (mainImage && mainImage.src && !mainImage.src.includes('data:')) {
        productImageUrl = mainImage.src;
        console.log('rambo-ring-widget: ✓✓✓ Using main displayed image:', productImageUrl);
      }
    }
    
    // PRIORITY 4: Fallback to swatch image from ringConfig
    if (!productImageUrl && style.swatchImage) {
      productImageUrl = style.swatchImage;
      console.log('rambo-ring-widget: Using swatch image from config as fallback:', productImageUrl);
    }
    
    if (!productImageUrl) {
      console.warn('rambo-ring-widget: ⚠️ No image found - will use default variant image');
    } else {
      console.log('rambo-ring-widget: ✓✓✓ FINAL IMAGE SELECTED:', productImageUrl);
    }
    
    if (productImageUrl) {
      // Ensure https protocol
      if (productImageUrl.startsWith('//')) {
        productImageUrl = 'https:' + productImageUrl;
      }
      
      // Clean up and normalize Shopify CDN URLs
      if (productImageUrl.includes('cdn.shopify.com') || productImageUrl.includes('shopifycdn.com')) {
        // Remove existing width parameter for full resolution
        productImageUrl = productImageUrl.replace(/([&?]width=\d+)/gi, '');
      }
      
      console.log('rambo-ring-widget: ✓✓✓ FINAL image for cart:', productImageUrl);
      properties['_image'] = productImageUrl;
    } else {
      console.warn('rambo-ring-widget: No product image found for style index', styleIndex);
    }

    // Add to cart via AJAX
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantIdToUse,
        quantity: 1,
        properties: properties
      })
    })
    .then(async response => {
      if (!response.ok) {
        const text = await response.text();
        let err = {};
        try {
          err = JSON.parse(text);
        } catch (e) {
          // Response was not JSON
        }
        
        let errorMessage = err.description || err.message;
        if (!errorMessage) {
          if (response.status === 409 || response.status === 422) {
             errorMessage = `Item is likely out of stock (HTTP ${response.status}).`;
          } else {
             errorMessage = `HTTP ${response.status}`;
          }
        }
        
        throw new Error(errorMessage);
      }
      return response.json();
    })
    .then(data => {
      // Show success toast
      showToast('Item added to cart successfully', 'success', 'Added to Cart');
      
      // Refresh cart count if there's a cart count element
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          // Update cart count badges
          const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, #cart-count');
          cartCountElements.forEach(el => {
            el.textContent = cart.item_count || 0;
          });
          
          // Trigger cart update event for theme
          const cartUpdateEvent = new CustomEvent('cart:updated', { detail: cart });
          document.dispatchEvent(cartUpdateEvent);
          
          // Dispatch additional events that themes might listen to
          document.dispatchEvent(new CustomEvent('cart:add', { detail: { cart: cart } }));
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          
          // Try multiple methods to open cart drawer
          let drawerOpened = false;
          
          // Method 1: Try cart-drawer custom element
          const cartDrawer = document.querySelector('cart-drawer, #cart-drawer, [id*="cart-drawer"]');
          if (cartDrawer) {
            if (typeof cartDrawer.open === 'function') {
              cartDrawer.open();
              drawerOpened = true;
            } else if (cartDrawer.classList) {
              cartDrawer.classList.add('is-open', 'active', 'open');
              drawerOpened = true;
            }
          }
          
          // Method 2: Try Shopify theme cart API
          if (!drawerOpened && window.Shopify?.theme?.cart?.open) {
            window.Shopify.theme.cart.open();
            drawerOpened = true;
          }
          
          // Method 3: Try Drawer API (older themes)
          if (!drawerOpened && window.Shopify?.Drawer?.open) {
            window.Shopify.Drawer.open({ id: 'CartDrawer' });
            drawerOpened = true;
          }
          
          // Method 4: Try to trigger click on cart icon/button
          if (!drawerOpened) {
            const cartIcon = document.querySelector('[data-cart-icon], .cart-icon, [aria-label*="cart" i], [aria-label*="Cart" i]');
            if (cartIcon) {
              cartIcon.click();
              drawerOpened = true;
            }
          }
          
          // Method 5: Try to find and open drawer by class/attribute
          if (!drawerOpened) {
            const drawer = document.querySelector('.drawer--cart, [data-drawer="cart"], .cart-drawer');
            if (drawer) {
              drawer.classList.add('is-open', 'active', 'open');
              drawer.setAttribute('aria-hidden', 'false');
              drawerOpened = true;
            }
          }
          
          // Last resort: redirect to cart page only if drawer couldn't be opened
          if (!drawerOpened) {
            window.location.href = '/cart';
          }
        })
        .catch(err => {
          // Still try to open cart drawer even if refresh failed
          const cartDrawer = document.getElementById('cart-drawer');
          if (cartDrawer && cartDrawer.open) {
            cartDrawer.open();
          } else if (window.Shopify?.theme?.cart?.open) {
            window.Shopify.theme.cart.open();
          } else {
            window.location.href = '/cart';
          }
        });
    })
    .catch(error => {
      showToast(error.message || 'Please try again.', 'error', 'Failed to Add to Cart');
    })
    .finally(() => {
      // Re-enable button
      if (addToCartButton) {
        addToCartButton.disabled = false;
        addToCartButton.textContent = 'Add to cart';
      }
      
      // Reset processing flag
      if (sectionEl) {
        sectionEl.dataset.isProcessing = 'false';
      }
    });
  }

  /**
   * Update URL parameters to reflect current selection
   */
  function updateUrlParams(styleLabel, styleIndex, styleCode, sizeCode, engravingText, engravingFont) {
    try {
      console.log('[RamboWidget] updateUrlParams called with:', {
        styleLabel,
        styleIndex,
        styleCode: styleCode || '(empty)',
        sizeCode: sizeCode || '(empty)',
        engravingText: engravingText ? engravingText.substring(0, 10) + '...' : '(empty)',
        engravingFont: engravingFont || '(empty)'
      });
      
      const newUrl = new URL(window.location.href);
      if (styleLabel) {
        newUrl.searchParams.set('ring_style', styleLabel);
      } else {
        newUrl.searchParams.delete('ring_style');
      }
      
      if (styleIndex !== undefined && styleIndex !== null && styleIndex >= 0) {
        newUrl.searchParams.set('style_index', styleIndex);
      } else if (styleIndex === null) {
        newUrl.searchParams.delete('style_index');
      }
      
      // Update prefilter_style_code when style changes
      if (styleCode) {
        newUrl.searchParams.set('prefilter_style_code', styleCode);
        console.log('[RamboWidget] ✅ Added prefilter_style_code to URL:', styleCode);
      } else {
        newUrl.searchParams.delete('prefilter_style_code');
      }
      
      if (sizeCode) {
        newUrl.searchParams.set('ring_size', sizeCode);
        console.log('[RamboWidget] ✅ Added ring_size to URL:', sizeCode);
      } else {
        newUrl.searchParams.delete('ring_size');
        console.warn('[RamboWidget] ⚠️ No sizeCode provided, removing ring_size from URL');
      }
      
      // Add engraving text and font to URL
      if (engravingText) {
        newUrl.searchParams.set('engraving_text', engravingText);
      } else {
        newUrl.searchParams.delete('engraving_text');
      }
      
      if (engravingFont) {
        newUrl.searchParams.set('engraving_font', engravingFont);
      } else {
        newUrl.searchParams.delete('engraving_font');
      }
      
      window.history.replaceState({}, '', newUrl);
      console.log('[RamboWidget] ✅ URL updated:', newUrl.toString());
      console.log('[RamboWidget] ✅ URL params:', {
        prefilter_style_code: newUrl.searchParams.get('prefilter_style_code'),
        style_index: newUrl.searchParams.get('style_index'),
        ring_style: newUrl.searchParams.get('ring_style'),
        ring_size: newUrl.searchParams.get('ring_size'),
        engraving_text: newUrl.searchParams.get('engraving_text'),
        engraving_font: newUrl.searchParams.get('engraving_font')
      });
    } catch (e) {
      console.warn('[RamboWidget] Failed to update URL:', e);
    }
  }

  /**
   * Show a toast notification
   */
  function showToast(message, type = 'success', title = '') {
    // Remove existing toast
    const existingToast = document.querySelector('.rambo-toast');
    if (existingToast) {
      existingToast.remove();
    }

    const toast = document.createElement('div');
    toast.className = `rambo-toast rambo-toast--${type}`;
    
    let iconHtml = '';
    if (type === 'success') {
      iconHtml = `<div class="rambo-toast__icon"><svg viewBox="0 0 20 20" fill="currentColor" style="color: #4caf50;"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg></div>`;
    } else {
      iconHtml = `<div class="rambo-toast__icon"><svg viewBox="0 0 20 20" fill="currentColor" style="color: #d20000;"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path></svg></div>`;
    }

    toast.innerHTML = `
      ${iconHtml}
      <div class="rambo-toast__content">
        ${title ? `<div class="rambo-toast__title">${title}</div>` : ''}
        <div class="rambo-toast__message">${message}</div>
      </div>
    `;

    document.body.appendChild(toast);

    // Trigger animation
    requestAnimationFrame(() => {
      toast.classList.add('show');
    });

    // Auto hide
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 300);
    }, 4000);
  }

  /**
   * Force update the main hero image to match the selected style
   * This ensures the large displayed image matches the selection even if gallery scrolling fails
   */
  function updateMainHeroImage(styleNumber, sectionEl) {
    if (!sectionEl || !styleNumber) return;

    // Find all potential image sources
    const allImages = sectionEl.querySelectorAll('.product-gallery__media img, .product-gallery__thumbnail img');
    let heroSrc = null;

    // Look for image with matching style number and "hero" in filename
    // Pattern: style01-hero, style-01-hero, etc.
    for (const img of allImages) {
      const src = img.currentSrc || img.src || '';
      const lowerSrc = src.toLowerCase();
      const filename = lowerSrc.split('/').pop().split('?')[0];
      
      if (filename.includes(`style${styleNumber}-hero`) || 
          filename.includes(`style-${styleNumber}-hero`) ||
          filename.includes(`style_${styleNumber}_hero`)) {
        heroSrc = src;
        break;
      }
    }

    if (heroSrc) {
       // Clean width params
       heroSrc = heroSrc.replace(/([&?]width=\d+)/gi, '');
       
       // Find the main display image(s)
       // Try common selectors for the "featured" or currently visible large image
       const mainImages = sectionEl.querySelectorAll(`
         .product-gallery__media[style*="display: block"] img,
         .product-gallery__media:not([hidden]) img,
         .product-gallery__featured-media img,
         .product__media-item--featured img,
         [data-product-media-type="image"] img
       `);

       mainImages.forEach(img => {
          // Only update if it's a "main" looking image (not a small thumbnail)
          // Simple heuristic: width > 200 or it's the only one
          if (img.clientWidth > 200 || mainImages.length === 1) {
             img.src = heroSrc;
             if (img.srcset) img.srcset = heroSrc; // Nuke srcset to prevent browser reverting
          }
       });
       
       // Also look for the specifically "active" or "current" media item if using a slider
       const activeSlide = sectionEl.querySelector('.is-active .product-gallery__image img, .swiper-slide-active img');
       if (activeSlide) {
          activeSlide.src = heroSrc;
          if (activeSlide.srcset) activeSlide.srcset = heroSrc;
       }
    }
  }

  /**
   * Escape HTML to prevent XSS
   */
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRamboRingWidget);
  } else {
    initRamboRingWidget();
  }

  // Re-initialize on back/forward navigation (bfcache)
  window.addEventListener('pageshow', function(event) {
    if (event.persisted) {
      // Page was restored from bfcache, reset flag and re-initialize
      widgetInitialized = false;
      initRamboRingWidget();
    }
  });

  // Also re-initialize on dynamic content updates (for SPA themes)
  if (window.Shopify?.theme?.events) {
    window.Shopify.theme.events.subscribe('product:loaded', function() {
      widgetInitialized = false;
      initRamboRingWidget();
    });
  }

  // Widget initialized silently
  
  // Handle browser back/forward navigation (popstate event)
  // This ensures visual state is restored when navigating via browser history
  window.addEventListener('popstate', function(event) {
    console.log('[RamboWidget] 🔄 Browser navigation detected (popstate), checking visual state...');
    restoreVisualState();
  });
  
  // Handle pageshow event (fires when page is loaded from cache, including back button)
  window.addEventListener('pageshow', function(event) {
    if (event.persisted) {
      console.log('[RamboWidget] 🔄 Page loaded from cache (pageshow), checking visual state...');
      // Delay to ensure DOM is ready, then restore multiple times
      setTimeout(() => {
        restoreVisualState();
      }, 100);
      setTimeout(() => {
        restoreVisualState();
      }, 500);
      setTimeout(() => {
        restoreVisualState();
      }, 1000);
    }
  });
  
  // Function to restore visual state based on widgetState
  function restoreVisualState() {
    // Use the centralized function
    forceSetVisualState();
  }

})();

